
import {
    PropType,
    computed,
    defineComponent,
    nextTick,
    onMounted,
    onUnmounted,
    ref,
    watch,
    onBeforeMount,
    getCurrentInstance,
} from 'vue'
import BaseInputOutlined from '@/components/inputs/BaseInputOutlined.vue'
import get from 'lodash/get'
import { Connector } from '@/types/main'
import { useProject } from '@root/src/hooks/useProject'
import { fetchDhMigrationQr, canTryDhMigration } from '@/helpers/channels/dhMigration'
import api from '@/api/api'
import SpinnerLoader from '@/components/SpinnerLoader.vue'
import SmallHint from '@/components/SmallHint.vue'
import ValidationInput from '@/components/inputs/ValidationInput.vue'
import SmsChannelSettings from '@/components/channels/SmsChannelSettings.vue'
import phoneValidator, { isValidPhone } from '@/helpers/validators/phoneValidator'
import { CONNECTOR_TYPES, PROVIDER_TYPES } from '@root/src/vars/general'
import { getInstance } from '@root/src/auth'
import ClockIcon from '@/assets/vuetifyIcons/clock.vue'
import { sendMassMail } from '@root/src/api/mailing'
import {
    fetchConnector,
    getWhatsappConnectorSyncModal,
    whatsappConnectorSyncModalToggle,
} from '@/api/projects'
import CustomActivatorSelect from '@/components/inputs/CustomActivatorSelect.vue'
import { CHANNEL_TYPES } from '@root/src/vars/general'
import cfg from '@root/config'
import { getQrCode } from '@/api/connectors/whatsapp'
import QRCode from 'qrcode'
import websocket from '@root/src/websockets'
import { formatPhoneNumber } from '@/helpers/formatters/phoneFormatter'
import { useBillingStore } from '@/store/stores/billing'
import DetachChannelModal from '@/modules/marketplace/components/billing/DetachChannelModal.vue'
import { toggleSubscription } from '@/api/marketplace'
import {
    applyCancelSubscriptionQueryParams,
    getConnectorChannelDisplayName,
} from '@/helpers/channels/getConnectorChannelDisplayName'
import { getProviderOptionsByGroup } from '@/helpers/channels/channelCatalog'
import { ensureProjectBillingLoaded } from '@/helpers/channels/ensureProjectBillingLoaded'
import { checkRole } from '@/helpers/roles'
import { logTgConnectionStage } from '@/helpers/ymLogger'
import { isProjectChatBotConnector } from '@/utils/subscribersGrowthWidget'

// Маппинг providerType на bot provider
// Рычаг поддержки: назначенный руками сторонний провайдер (см. forcedProvider в
// документе коннектора). Обычная разводка ведёт на наш, этот список — исключение.
const FORCED_PROVIDER_ROUTE = {
    [PROVIDER_TYPES.whatsappBasis]: 'basis-api',
    [PROVIDER_TYPES.whatsappApi]: 'wApi-whatsapp',
    [PROVIDER_TYPES.telegramApi]: 'wApi-telegram',
    [PROVIDER_TYPES.maxBasis]: 'green-api-max',
}

const PROVIDER_TO_BOT_PROVIDER_MAP = {
    // Провайдеры 1 и 2 у WhatsApp выведены из выбора: канал ведёт наш dh-whatsapp,
    // а бэк в getDhWaQr держит канал у стороннего, пока тот авторизован, и забирает
    // его себе в любом другом случае. Тот же приём, что уже сделан для telegramApi.
    [PROVIDER_TYPES.whatsappBasis]: 'dh-whatsapp',
    [PROVIDER_TYPES.whatsappApi]: 'dh-whatsapp',
    // Provider 1 (wApi-telegram) is deprecated: legacy telegramApi channels behave
    // as dh-telegram (QR + code + 2FA via provider 2). The backend keeps them on
    // wApi only while authorized and otherwise migrates them under the hood.
    [PROVIDER_TYPES.telegramApi]: 'dh-telegram',
    [PROVIDER_TYPES.telegramDh]: 'dh-telegram',
    [PROVIDER_TYPES.maxBasis]: 'dh-max',
    [PROVIDER_TYPES.maxDh]: 'dh-max',
    [PROVIDER_TYPES.maxBusiness]: 'max-business',
    [PROVIDER_TYPES.smsGate]: 'sms-gate',
    [PROVIDER_TYPES.whatsappDh]: 'dh-whatsapp',
}

// Маппинг bot provider на display name
const BOT_PROVIDER_TO_NAME_MAP = {
    'basis-api': 'WhatsApp 1',
    'wApi-whatsapp': 'WhatsApp 2',
    'dh-whatsapp': 'WhatsApp 3',
    'wApi-telegram': 'Telegram',
    'dh-telegram': 'Telegram',
    'green-api-max': 'MAX 1',
    'dh-max': 'MAX 2',
    'max-business': 'MAX Business',
    'sms-gate': 'SMS',
}

const BOT_PROVIDERS_WITH_CONTROL = [
    'wApi-telegram',
    'basis-api',
    'wApi-whatsapp',
    'green-api-max',
    'dh-telegram',
    'dh-max',
    'dh-whatsapp',
]

export default defineComponent({
    props: {
        value: {
            type: Object,
            required: true,
        },
        appCreated: {
            type: Boolean,
            default: false,
        },
        connector: {
            type: Object as PropType<Connector>,
            required: true,
        },
        yClientsConnector: {
            type: Object as PropType<Connector>,
            required: false,
        },
        providerType: {
            type: String,
            required: true,
        },
        mode: {
            type: String,
            default: 'create',
        },
        inSidebar: {
            type: Boolean,
            default: false,
        },
        isOnboarding: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input', 'saved', 'authorized', 'auth-status-changed', 'ready'],
    components: {
        BaseInputOutlined,
        SpinnerLoader,
        SmallHint,
        ValidationInput,
        SmsChannelSettings,
        ClockIcon,
        CustomActivatorSelect,
        DetachChannelModal,
    },
    setup(props, { emit }) {
        const unAuthInProcess = ref(false)
        const root = getCurrentInstance().proxy
        const { project } = useProject()
        const billingStore = useBillingStore()

        // Переезд на наш провайдер (см. helpers/channels/dhMigration.ts): вместо QR старого
        // провайдера показываем QR нашего инстанса. Включён переезд или нет — знает только
        // бэк (поле isDhMigration у проекта либо дефолт мессенджера), поэтому для канала на
        // стороннем провайдере пробуем ручку переезда, а на отказ встаём в dhMigrationFallback
        // и дальше идём как раньше.
        const dhMigrationFallback = ref(false)
        const isDhMigration = computed(
            () => !dhMigrationFallback.value && canTryDhMigration(props.providerType)
        )
        const fetchQrInterval = ref<any>()
        const phonePollInterval = ref<any>()
        const qr = ref({ loading: true, data: {} as any })
        const isSettingsDataReady = ref(!!props.isOnboarding)
        const slotFreezeLoaded = ref(false)
        const initialStatusLoaded = ref(false)
        const testMessage = ref({ phone: '', loading: false, status: null as 'success' | 'error' | null })
        const selectedBotProvider = ref('')
        const slotDeletedDate = ref<string | null>(null)
        const slotNextPayment = ref<string | null>(null)
        const currentSlotId = ref<string | null>(null)
        const currentSlotSubscriptionId = ref<number | null>(null)
        const currentSlotIsTariff = ref(false)
        const currentSlotRenewalMode = ref<string | null>(null)
        const currentSlotAutoRenewalEnabled = ref(false)
        const phone = ref('') // Номер телефона, получаемый из запросов (не из props)
        const phoneKey = ref(0)
        const wasTimezoneAdded = ref(false)
        const phoneToControl = ref('')
        const phoneToControlList = ref([])
        const phoneToControlKey = ref(0)
        const isControlErrorShow = ref(false)
        const tgDhInstanceId = ref(null)
        const tgStatusChecked = ref(false) // Флаг для отслеживания, была ли проверка статуса Telegram выполнена
        const providerChanging = ref(false) // Флаг загрузки при смене провайдера
        const selectedProviderForChange = ref('') // Выбранный провайдер для смены
        const hasSavedOnAuth = ref(false) // Флаг, что сохранение уже было выполнено при авторизации
        const lastEmittedAuthStatus = ref<boolean | null>(null)
        const isDetachChannelModalOpened = ref(false)
        const detachChannelModalLoading = ref(false)
        const detachChannelModalChannelName = ref('канал')
        const detachChannelModalSubscriptionId = ref<number | null>(null)
        const maxBusinessToken = ref('')
        const maxBusinessTokenError = ref('')
        const maxBusinessTokenLoading = ref(false)
        const smsGateUsername = ref('')
        const smsGatePassword = ref('')
        const smsGateDeviceId = ref('')
        const smsGatePhone = ref('')
        const smsGateUsernameReadonly = ref(true)
        const smsGatePasswordReadonly = ref(true)
        const smsGateCredentialsError = ref('')
        const smsGateCredentialsLoading = ref(false)

        // SMS-v2 QR-онбординг
        const smsGateQrDataUrl = ref('')
        const smsGateQrLoading = ref(false)
        const smsGateQrError = ref('')

        // Собрать QR: дёрнуть { url, token } из BFF и нарисовать картинку клиентом.
        // Само подключение детектит существующий поллинг getSmsGateStatus (data:true = deviceId в канале).
        const loadSmsGateQr = async () => {
            const connectorId = props.connector?.connectorId
            if (!connectorId) return
            smsGateQrLoading.value = true
            smsGateQrError.value = ''
            try {
                const res = await api.networkFixer.getSmsGateQr({ connectorId })
                const payload = res?.data
                if (!payload || res?.error || !payload.url || !payload.token) {
                    smsGateQrError.value = 'Не удалось получить QR. Попробуйте обновить.'
                    smsGateQrDataUrl.value = ''
                    return
                }
                // Приложение сканит JSON { url, token }
                const qrText = JSON.stringify({ url: payload.url, token: payload.token })
                smsGateQrDataUrl.value = await QRCode.toDataURL(qrText, {
                    margin: 2,
                    width: 240,
                    errorCorrectionLevel: 'M',
                })
            } catch (e) {
                smsGateQrError.value = 'Не удалось получить QR. Попробуйте обновить.'
                smsGateQrDataUrl.value = ''
            } finally {
                smsGateQrLoading.value = false
            }
        }

        // SMS-v2: номер SIM вводит клиент вручную.
        // Из карты его не достать: MSISDN пишется в необязательный EF_MSISDN, и
        // российские операторы его практически не заполняют (проверено вживую).
        // Номер нужен только для отображения и тестовой отправки — рассылка идёт
        // по deviceId+simNumber, входящие резолвятся по token.
        // Номер SIM, реально сохранённый в канале (приходит из ручки статуса).
        // Именно он, а не phone/локальный ввод, решает, показывать ли тестовую отправку.
        // Онлайн/офлайн + карточка устройства. online — ОТДЕЛЬНЫЙ признак от привязки.
        const smsGateStatusLoaded = ref(false)
        const smsGateOnline = ref(null)
        const smsGateLastSeen = ref('')
        const smsGateDeviceName = ref('')
        const smsGateSimCards = ref([])
        const smsGateSimNumber = ref(null)
        const smsGateSimPhones = ref({})
        const smsGateSimSaving = ref(false)
        const smsGateSimError = ref('')
        const smsGateChannelPhone = ref('')
        const smsGatePhoneInput = ref('')
        const smsGatePhoneSaving = ref(false)
        const smsGatePhoneError = ref('')

        const saveSmsGatePhone = async () => {
            const value = smsGatePhoneInput.value?.trim()
            if (!isValidPhone(value)) {
                smsGatePhoneError.value = 'Введите корректный номер телефона'
                return
            }

            smsGatePhoneSaving.value = true
            smsGatePhoneError.value = ''
            try {
                const res = await api.networkFixer.setChannelPhone({
                    connectorId: props.connector.connectorId,
                    phone: value,
                })

                if (res?.error) {
                    smsGatePhoneError.value = 'Не удалось сохранить номер. Попробуйте ещё раз.'
                    return
                }

                const savedPhone = res?.data?.phone || value.replace(/[^\d+]/g, '')
                const currentConnector =
                    root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                root.$store.commit('updateConnector', {
                    ...currentConnector,
                    phone: savedPhone,
                })
                phone.value = savedPhone
                smsGateChannelPhone.value = savedPhone
                if (smsGateSimNumber.value) {
                    smsGateSimPhones.value = {
                        ...smsGateSimPhones.value,
                        [String(smsGateSimNumber.value)]: savedPhone,
                    }
                }
                emit('input', Object.assign({}, props.value, { phone: savedPhone }))
                phoneKey.value++
            } catch (e) {
                smsGatePhoneError.value = 'Не удалось сохранить номер. Попробуйте ещё раз.'
            } finally {
                smsGatePhoneSaving.value = false
            }
        }

        const unlockSmsGateUsername = () => {
            smsGateUsernameReadonly.value = false
        }

        const unlockSmsGatePassword = () => {
            smsGatePasswordReadonly.value = false
        }

        const resetSmsGateCredentialsFields = () => {
            smsGateUsername.value = ''
            smsGatePassword.value = ''
            smsGateDeviceId.value = ''
            smsGatePhone.value = ''
            smsGateUsernameReadonly.value = true
            smsGatePasswordReadonly.value = true
        }
        // Проверяем, был ли канал уже авторизован в store при создании компонента
        const wasAuthorizedOnMount = ref(props.connector?.auth === true || false)
        const isInitializing = ref(true) // Флаг, что компонент еще инициализируется
        const tg1CodeWaiting = ref(false)
        const tg1CodeSending = ref(false)
        const telegramAuthMethod = ref<'code' | 'qr'>('qr') // Метод авторизации Telegram: 'code' (ввод кода) или 'qr' (QR-код)
        // Временно скрыт способ подключения Telegram «по коду» — оставлен только QR.
        // Вернуть переключатель = поставить true.
        const telegramCodeAuthEnabled = ref(false)
        const hasWhatsappQrInCurrentAuthFlow = ref(false)
        const isWhatsappSyncModalOpened = ref(false)
        const hideWhatsappSyncModal = ref(false)
        const isWhatsappSyncModalHidden = ref(false)
        const isWhatsappSyncModalPreferenceLoaded = ref(false)

        const tgSendCode = ref({
            phone: '',
            isSendCode: false,
            countdown: 60,
            condition: 'noAuth',
            code: '',
            showSendBtn: false,
            error: null,
            // 2FA: облачный пароль (показывается после ответа password_needed)
            passwordMode: false,
            password: '',
            showPassword: false
        })

        // Дедуп целей Яндекс.Метрики по этапам подключения Telegram:
        // poll идёт каждые 5 сек, поэтому каждую цель шлём один раз за попытку.
        const tgYmGoalsSent = ref({ qrShown: false, twoFa: false, authorized: false })
        
        // Отдельные поля для ввода кода (5 цифр)
        const codeDigits = ref(['', '', '', '', ''])
        const maxSendCode = ref({
            phone: '',
            isSendCode: false,
            countdown: 60,
            condition: 'noAuth',
            code: '',
            showSendBtn: false,
            currentError: null,
            sendReqInProcess: false,
            sendCodeError: false
        })
        
        // Сохраняем исходный провайдер при инициализации
        const originalProviderType = ref<string | null>(null)
        const lastDataHeroesQrRequestTime = ref<boolean | null>(null) // Время последнего запроса QR-кода для Data Heroes
        const lastTgQrRequestTime = ref<number | null>(null) // Время последнего запроса QR-кода для Telegram
        const lastMaxQrRequestTime = ref<number | null>(null) // Время последнего запроса QR-кода для MAX
        const lastWa2QrRequestTime = ref<number | null>(null) // Время последнего запроса QR-кода для WA 2

        // Определяем bot provider на основе providerType. Рычаг поддержки forcedProvider
        // из документа коннектора перебивает разводку: канал идёт к назначенному руками
        // провайдеру, а бэк такой канал не забирает.
        const getBotProviderFromProviderType = (providerType: string) => {
            const forced = (props.connector as any)?.forcedProvider
            if (forced && FORCED_PROVIDER_ROUTE[forced]) {
                return FORCED_PROVIDER_ROUTE[forced]
            }
            return PROVIDER_TO_BOT_PROVIDER_MAP[providerType] || ''
        }

        const connectedBot = computed(() => {
            // Для провайдера Data Heroes возвращаем специальный текст
            if (isDataHeroesProvider.value) {
                return 'Провайдер DH (только маркетинговые рассылки)'
            }
            
            // Используем selectedBotProvider, если он установлен, иначе определяем из providerType
            const botProvider = selectedBotProvider.value || getBotProviderFromProviderType(props.providerType)
            
            // Telegram no longer exposes a provider split (provider 1 is retired and
            // legacy channels migrate to provider 2 under the hood) — show plain "Telegram".
            if (botProvider === 'wApi-telegram' || botProvider === 'dh-telegram') return 'Telegram'
            if (botProvider === 'basis-api') return 'Провайдер 1'
            if (botProvider === 'wApi-whatsapp') return 'Провайдер 2'
            if (botProvider === 'dh-whatsapp') return 'Провайдер 3'
            if (botProvider === 'green-api-max') return 'Провайдер 1'
            if (botProvider === 'dh-max') return 'Провайдер 2'
            if (botProvider === 'max-business') return 'MAX Business'
            return ''
        })

        // Форматированный номер телефона для отображения
        const formattedPhone = computed(() => {
            return formatPhoneNumber(phone.value)
        })
        const effectivePhone = computed(() => {
            const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
            return phone.value || currentConnector?.phone || props.connector?.phone || ''
        })
        const formattedEffectivePhone = computed(() => {
            return formatPhoneNumber(effectivePhone.value)
        })
        
        const formattedTgSendCodePhone = computed(() => {
            return formatPhoneNumber(tgSendCode.value.phone)
        })
        const slotPayUntilLabel = computed(() => {
            if (!slotDeletedDate.value) return ''
            const dt = new Date(slotDeletedDate.value)
            if (Number.isNaN(dt.getTime())) return ''
            dt.setDate(dt.getDate() - 1)
            return dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
        })
        const slotPaidUntilLabel = computed(() => {
            if (!slotNextPayment.value) return ''
            const dt = new Date(slotNextPayment.value)
            if (Number.isNaN(dt.getTime())) return ''
            return dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
        })
        const slotNextPaymentShortLabel = computed(() => {
            if (!slotNextPayment.value) return ''
            const dt = new Date(slotNextPayment.value)
            if (Number.isNaN(dt.getTime())) return ''
            return dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })
        })
        const tariffEndDateLabel = computed(() => {
            const projectId = props.connector?.projectId
            if (!projectId) return ''
            const raw = billingStore.getBlockedTariffPayUntil(projectId)
            if (!raw) return ''
            const dt = new Date(raw)
            if (Number.isNaN(dt.getTime())) return ''
            return dt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
        })
        const isNoTariffBillingState = computed(() => {
            const projectId = props.connector?.projectId
            if (!projectId) return false
            return !!project.value?.billingEnabled && !billingStore.getCurrentTariffState(projectId)
        })
        const slotBillingBannerMode = computed<'frozen' | 'paid' | null>(() => {
            if (props.isOnboarding) return null
            if (isNoTariffBillingState.value) return 'frozen'
            if (slotPayUntilLabel.value) return 'frozen'
            if (slotPaidUntilLabel.value) return 'paid'
            return null
        })
        const onboardingQrSteps = computed(() => {
            if (!props.isOnboarding) return []
            const provider = selectedBotProvider.value
            if (provider === 'green-api-max' || provider === 'dh-max') {
                return [
                    'Откройте MAX на своём основном телефоне',
                    'Перейдите в «Профиль» MAX',
                    'Перейдите в «Устройства»',
                    'Нажмите «Войти по QR-коду»',
                ]
            }
            if (provider === 'wApi-telegram' || provider === 'dh-telegram') {
                if (telegramAuthMethod.value !== 'qr') return []
                return [
                    'Откройте Telegram на своём основном телефоне',
                    'Перейдите в «Настройки» Telegram',
                    'Перейдите в «Устройства»',
                    'Нажмите «Подключить устройство»',
                ]
            }
            return [
                'Откройте WhatsApp на своём основном телефоне',
                'Android: нажмите 3 точки\niPhone: перейдите в Настройки WhatsApp',
                'Перейдите в «Связанные устройства»',
                'Нажмите «Привязка устройства»',
            ]
        })
        const showSlotBillingBanner = computed(() => !!slotBillingBannerMode.value)
        const slotBillingBannerDate = computed(() => {
            if (slotBillingBannerMode.value === 'frozen') return slotPayUntilLabel.value
            if (slotBillingBannerMode.value === 'paid') return slotPaidUntilLabel.value
            return ''
        })
        const slotBillingBannerText = computed(() => {
            if (isNoTariffBillingState.value && tariffEndDateLabel.value) return `Оплатить до: ${tariffEndDateLabel.value}`
            if (isNoTariffBillingState.value) return `Оплатите тариф`
            if (slotBillingBannerMode.value === 'frozen' && slotBillingBannerDate.value) return `Оплатить до: ${slotBillingBannerDate.value}`
            if (slotBillingBannerMode.value === 'frozen') return `Оплатите канал или тариф`
            if (currentSlotIsTariff.value) {
                // На узких экранах убираем «Тариф оплачен», чтобы дата не съедалась ellipsis.
                const isNarrow = (root.$vuetify?.breakpoint?.width || 0) < 480
                return isNarrow
                    ? `До: ${slotBillingBannerDate.value}`
                    : `Тариф оплачен до: ${slotBillingBannerDate.value}`
            }
            return `Оплачен до: ${slotBillingBannerDate.value}`
        })
        const currentSlotAutoRenewalMenuText = computed(() =>
            currentSlotAutoRenewalEnabled.value ? 'Выключить автопродление' : 'Включить автопродление'
        )
        const slotBillingBannerWarningText = computed(() => {
            if (isNoTariffBillingState.value) return `
                Тариф не оплачен и все каналы заморожены. Продлите подписку, чтобы возобновить работу.
            `
            return `Все отправки с данного канала приостановленны. Клиенты могут перестать получать сообщения, если это ваш единственный канал отправки. Оплатите подписку на канал, чтобы восстановить отправки.`
        })
        const getDontShowAgainQRValue = (data: any) => {
            const value = Array.isArray(data)
                ? data[0]?.dontShowAgainQR
                : data?.dontShowAgainQR ?? data?.data?.dontShowAgainQR ?? data
            return value === true
        }
        const loadWhatsappSyncModalPreference = async () => {
            const projectId = props.connector?.projectId
            if (!projectId || !isWhatsappChannel.value) {
                isWhatsappSyncModalPreferenceLoaded.value = true
                return
            }
            const { data, error } = await getWhatsappConnectorSyncModal(projectId)
            if (!error) {
                isWhatsappSyncModalHidden.value = getDontShowAgainQRValue(data)
            }
            isWhatsappSyncModalPreferenceLoaded.value = true
        }
        const setWhatsappSyncModalPreference = async (dontShowAgainQR: boolean) => {
            const projectId = props.connector?.projectId
            if (!projectId) return
            const previousValue = isWhatsappSyncModalHidden.value
            isWhatsappSyncModalHidden.value = dontShowAgainQR
            const { error } = await whatsappConnectorSyncModalToggle({ projectId, dontShowAgainQR })
            if (error) {
                isWhatsappSyncModalHidden.value = previousValue
                hideWhatsappSyncModal.value = previousValue
                root.$store.dispatch('callNotify', 'Не удалось сохранить настройку')
            }
        }
        const setConnectorSidebarModalState = (opened: boolean) => {
            if (props.inSidebar) {
                root?.$root?.$emit('connector-settings-modal-state', opened)
            }
        }
        const rememberWhatsappQrShown = () => {
            if (isWhatsappChannel.value) {
                hasWhatsappQrInCurrentAuthFlow.value = true
            }
        }
        const showWhatsappSyncModalIfNeeded = async () => {
            if (!isWhatsappChannel.value || !hasWhatsappQrInCurrentAuthFlow.value) {
                return
            }
            if (!isWhatsappSyncModalPreferenceLoaded.value) {
                await loadWhatsappSyncModalPreference()
            }
            if (isWhatsappSyncModalHidden.value) {
                return
            }
            hideWhatsappSyncModal.value = false
            setConnectorSidebarModalState(true)
            isWhatsappSyncModalOpened.value = true
            hasWhatsappQrInCurrentAuthFlow.value = false
        }
        const closeWhatsappSyncModal = () => {
            if (!isWhatsappSyncModalOpened.value) return
            isWhatsappSyncModalOpened.value = false
            setTimeout(() => {
                setConnectorSidebarModalState(false)
            }, 100)
        }
        const handleWhatsappSyncModalInput = (opened: boolean) => {
            if (opened) {
                setConnectorSidebarModalState(true)
                isWhatsappSyncModalOpened.value = true
                return
            }
            closeWhatsappSyncModal()
        }
        const tryMarkSettingsDataReady = () => {
            if (props.isOnboarding || isSettingsDataReady.value) return
            if (!slotFreezeLoaded.value || !initialStatusLoaded.value) return
            if (props.providerType && !selectedBotProvider.value) return
            isSettingsDataReady.value = true
            emit('ready')
        }
        const loadSlotFreezeState = async () => {
            const projectId = props.connector?.projectId
            const connectorId = props.connector?.connectorId
            if (!projectId || !connectorId) {
                slotDeletedDate.value = null
                slotNextPayment.value = null
                currentSlotId.value = null
                currentSlotSubscriptionId.value = null
                currentSlotIsTariff.value = false
                currentSlotRenewalMode.value = null
                currentSlotAutoRenewalEnabled.value = false
                slotFreezeLoaded.value = true
                tryMarkSettingsDataReady()
                return
            }
            try {
                const { data, error } = await api.networkFixer.getSlots({ projectId })
                if (error || !Array.isArray(data)) {
                    slotDeletedDate.value = null
                    slotNextPayment.value = null
                    currentSlotId.value = null
                    currentSlotSubscriptionId.value = null
                    currentSlotIsTariff.value = false
                    currentSlotRenewalMode.value = null
                    currentSlotAutoRenewalEnabled.value = false
                    return
                }
                const slot = data.find((s: any) => s?.channelConnectorId === connectorId)
                slotDeletedDate.value = slot?.channelDeletedDate || null
                slotNextPayment.value = slot?.nextPayment || null
                currentSlotId.value = slot?.id ? String(slot.id) : null
                currentSlotSubscriptionId.value =
                    typeof slot?.subscriptionId === 'number' ? slot.subscriptionId : null
                currentSlotRenewalMode.value = slot?.renewalMode || null
                currentSlotAutoRenewalEnabled.value = slot?.isAutoRenewalEnabled === true
                const slotTariffValue = slot?.isTariff
                currentSlotIsTariff.value =
                    slotTariffValue === true ||
                    slotTariffValue === 1 ||
                    slotTariffValue === '1' ||
                    slotTariffValue === 'true'
            } catch (e) {
                slotDeletedDate.value = null
                slotNextPayment.value = null
                currentSlotId.value = null
                currentSlotSubscriptionId.value = null
                currentSlotIsTariff.value = false
                currentSlotRenewalMode.value = null
                currentSlotAutoRenewalEnabled.value = false
            } finally {
                slotFreezeLoaded.value = true
                tryMarkSettingsDataReady()
            }
        }
        const openTariffBilling = () => {
            const projectId = props.connector?.projectId
            if (!projectId) {
                root?.$store?.dispatch('callNotify', 'Проект не найден')
                return
            }
            if (isNoTariffBillingState.value) {
                root?.$router?.push(`/project/${projectId}/billing/?section=tariffs&highlighted=true`)
                return
            }
            billingStore.fetchCurrentTariff(projectId).then(() => {
                const current = billingStore.getCurrentTariff(projectId)
                if (!current?.integrationId) {
                    root?.$store?.dispatch('callNotify', 'Не удалось получить текущий тариф')
                    return
                }
                root?.$store?.commit('setChannelCreationMode', false)
                root?.$store?.commit('setChannelCreationStage', null)
                root?.$store?.commit('setIsConnectorSettingsSidebar', false)
                root?.$store?.commit('setConnectorSettingsSidebarId', null)
                billingStore.selectedTariffId = current.integrationId
                billingStore.selectedSubscriptionPeriod = String(current.periodInMonths || '12')

                if (currentSlotIsTariff.value || !currentSlotId.value || currentSlotRenewalMode.value === 'with_tariff') {
                    billingStore.setAddChannelContext(null)
                } else {
                    const channelMetaMap: Record<string, { name: string; kind: string; icon: string }> = {
                        [PROVIDER_TYPES.whatsappBasis]: {
                            name: 'WhatsApp',
                            kind: 'whatsapp',
                            icon: require('@/assets/icons/whatsApp-white.svg'),
                        },
                        [PROVIDER_TYPES.whatsappApi]: {
                            name: 'WhatsApp',
                            kind: 'whatsapp',
                            icon: require('@/assets/icons/whatsApp-white.svg'),
                        },
                        [PROVIDER_TYPES.whatsapp]: {
                            name: 'WhatsApp',
                            kind: 'whatsapp',
                            icon: require('@/assets/icons/whatsApp-white.svg'),
                        },
                        [PROVIDER_TYPES.telegramApi]: {
                            name: 'Telegram',
                            kind: 'telegram',
                            icon: require('@/assets/icons/telegram-white.svg'),
                        },
                        [PROVIDER_TYPES.telegramDh]: {
                            name: 'Telegram',
                            kind: 'telegram',
                            icon: require('@/assets/icons/telegram-white.svg'),
                        },
                        [PROVIDER_TYPES.maxBasis]: {
                            name: 'MAX',
                            kind: 'max',
                            icon: require('@/assets/icons/max-white.svg'),
                        },
                        [PROVIDER_TYPES.maxDh]: {
                            name: 'MAX',
                            kind: 'max',
                            icon: require('@/assets/icons/max-white.svg'),
                        },
                    }
                    const baseMeta = channelMetaMap[props.providerType] || {
                        name: 'WA/TG/MAX/SMS/SMS',
                        kind: 'bundle',
                        icon: null,
                    }
                    billingStore.setAddChannelContext({
                        ...baseMeta,
                        subscriptionId: currentSlotSubscriptionId.value,
                        renewalMode: currentSlotRenewalMode.value,
                    })
                }
                billingStore.setPaymentModalOpened(true, true)
            })
        }
        const onBannerChangeChannel = () => {
            const projectId = props.connector?.projectId
            if (!projectId) {
                root?.$store?.dispatch('callNotify', 'Проект не найден')
                return
            }
            if (currentSlotId.value === null) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить слот')
                return
            }

            root?.$store?.commit('setChannelCreationMode', false)
            root?.$store?.commit('setChannelCreationStage', null)
            root?.$store?.commit(
                'setConnectorSettingsSidebarId',
                `change-channel:${props.connector.connectorId}:${currentSlotId.value}`
            )
            root?.$store?.commit('setIsConnectorSettingsSidebar', true)
        }
        const enableSlotAutoRenewal = async () => {
            const projectId = props.connector?.projectId
            const subscriptionId = currentSlotSubscriptionId.value
            if (!projectId) {
                root?.$store?.dispatch('callNotify', 'Проект не найден')
                return
            }
            if (!subscriptionId) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить подписку')
                return
            }
            const { error } = await toggleSubscription({
                projectId,
                subscriptionId,
                status: 'enable',
            })
            if (error) {
                root?.$store?.dispatch('callNotify', 'Не удалось включить автопродление')
                return
            }
            await loadSlotFreezeState()
            root?.$store?.dispatch('callNotify', 'Автопродление включено')
        }
        const onBannerCancelSubscription = async () => {
            if (!currentSlotAutoRenewalEnabled.value) {
                await enableSlotAutoRenewal()
                return
            }
            const projectId = props.connector?.projectId
            if (!projectId) {
                root?.$store?.dispatch('callNotify', 'Проект не найден')
                return
            }
            applyCancelSubscriptionQueryParams({
                renewalMode: currentSlotRenewalMode.value,
                channelName: getConnectorChannelDisplayName(
                    props.connector,
                    project.value?.connectors || []
                ),
                periodTo:
                    slotNextPayment.value ||
                    billingStore.getTariffPeriodEnd(projectId) ||
                    null,
            })
            root?.$store?.commit('setChannelCreationMode', false)
            root?.$store?.commit('setChannelCreationStage', null)
            root?.$store?.commit('setConnectorSettingsSidebarId', `cancel-subscription:${props.connector.connectorId}`)
            root?.$store?.commit('setIsConnectorSettingsSidebar', true)
        }
        const closeDetachChannelModal = () => {
            isDetachChannelModalOpened.value = false
            detachChannelModalSubscriptionId.value = null
            detachChannelModalLoading.value = false
        }
        const handleDetachChannelCreatePackage = async () => {
            const subscriptionId = detachChannelModalSubscriptionId.value
            if (!subscriptionId) {
                root?.$store?.dispatch('callNotify', 'Не удалось определить канал для отвязки')
                return
            }
            detachChannelModalLoading.value = true
            const { error } = await api.networkFixer.unchainSlot({ subscriptionId })
            if (error) {
                detachChannelModalLoading.value = false
                root?.$store?.dispatch('callNotify', 'Ошибка при отвязке дополнительного канала')
                return
            }
            detachChannelModalLoading.value = false
            closeDetachChannelModal()
            await loadSlotFreezeState()
            root?.$store?.dispatch('callNotify', 'Канал переведен в отдельный пакет')
        }

        const onSmsWizardTest = (phone) => {
            testMessage.value.phone = phone || ''
            sendTestMesage()
        }

        const sendTestMesage = async () => {
            // Не позволяем отправлять тестовое сообщение во время смены провайдера
            if (providerChanging.value) {
                return
            }
            // Сбрасываем предыдущий статус перед отправкой
            testMessage.value.status = null
            try {
                testMessage.value.loading = true
                
                const messagePayload: any = {
                    from: phone.value.replace(/[^0-9]/g, ''),
                    to: testMessage.value.phone.replace(/[^0-9]/g, ''),
                    text: 'Тестовое сообщение',
                    communicationId: null,
                    sendingSettings: {
                        type: 'channel' as const,
                        id: props.connector?.channelId
                    },
                }
                
                const { data: sendMessageData, error: senMessageError } = await sendMassMail({
                    projectId: props.connector.projectId,
                    connectorId: props.connector.connectorId,
                    service: 'cascade',
                    sendingSettings: {
                        type: 'channel',
                        id: props.connector?.channelId
                    },
                    messages: [messagePayload],
                })
                if (senMessageError) {
                    testMessage.value.status = 'error'
                    testMessage.value.loading = false
                    // Скрываем статус через 3 секунды
                    setTimeout(() => {
                        testMessage.value.status = null
                    }, 3000)
                    return
                }
                // Сообщение успешно отправлено
                testMessage.value.status = 'success'
                testMessage.value.loading = false
                // Скрываем статус через 3 секунды
                setTimeout(() => {
                    testMessage.value.status = null
                }, 3000)
            } catch (err) {
                console.error(err)
                testMessage.value.status = 'error'
                testMessage.value.loading = false
                // Скрываем статус через 3 секунды
                setTimeout(() => {
                    testMessage.value.status = null
                }, 3000)
            }
        }

        const unAuthInstance = async () => {
            // Не позволяем отключать во время смены провайдера
            if (providerChanging.value) {
                return
            }
            try {
                unAuthInProcess.value = true
                testMessage.value.loading = true
                
                // Для провайдера Data Heroes используем специальный API
                if (isDataHeroesProvider.value) {
                    const { error } = await api.connectors.whatsapp.logout(props.connector.connectorId)
                    if (error) {
                        root.$store.dispatch('callNotify', 'Ошибка при отключении')
                        testMessage.value.loading = false
                        unAuthInProcess.value = false
                        return
                    }
                    // Отключение идёт по РЕАЛЬНОМУ провайдеру канала, а не по тому, у кого
                    // фронт просит QR: у канала на green-api разводка QR уже смотрит на нас,
                    // а инстанс ещё чужой, и наша ручка деавторизации на нём падает.
                } else if (props.providerType === PROVIDER_TYPES.maxDh) {
                    await api.networkFixer.deauthDhMaxChannel({
                        connectorId: props.connector.connectorId,
                    })
                } else if (props.providerType === PROVIDER_TYPES.whatsappDh) {
                    await api.networkFixer.deauthWaDhChannel({
                        connectorId: props.connector.connectorId,
                    })
                } else {
                    await api.networkFixer.unAuthInstanceRework({ 
                        connectorId: props.connector.connectorId,
                        phone: phone.value || ''
                    })
                }
                
                // Сбрасываем таймеры при отключении авторизации, чтобы запрос мог выполниться сразу
                lastDataHeroesQrRequestTime.value = null
                lastTgQrRequestTime.value = null
                lastMaxQrRequestTime.value = null
                lastWa2QrRequestTime.value = null
                
                qr.value.data = { type: 'qrCode' }
                phone.value = ''
                tgDhInstanceId.value = null
                tgStatusChecked.value = false // Сбрасываем флаг проверки статуса при отключении
                testMessage.value.loading = false
                emitAuthStatusChanged(false)
                // Обновление auth: false произойдет автоматически через watcher при изменении isAuthorized

                // SMS: у sms-провайдера нет QR-refresh цикла, который сбрасывает
                // unAuthInProcess обратно в false (как у WA/TG/Max). Без явного сброса
                // флаг залипает true после «Отключить», и UI не реагирует на повторный
                // скан QR до перезагрузки страницы (все next-step условия требуют
                // unAuthInProcess === false). Сбрасываем сразу — QR-шаг уже показан.
                if (isSmsGateProvider.value) {
                    unAuthInProcess.value = false
                }

                // MAX Business: QR нет, после отключения сразу показываем ввод токена.
                if (isMaxBusinessProvider.value) {
                    qr.value.data = { type: 'notAuthorized', status: 'notAuthorized' }
                    maxBusinessToken.value = ''
                    unAuthInProcess.value = false
                }

                // Останавливаем опрос телефона при отключении
                if (phonePollInterval.value) {
                    clearInterval(phonePollInterval.value)
                    phonePollInterval.value = null
                }
            } catch (e) {
                testMessage.value.loading = false
                root.$store.dispatch('callNotify', 'Ошибка при удалении авторизации')
            }
        }

        const connectMaxBusinessByToken = async () => {
            const token = maxBusinessToken.value?.trim()
            if (!token) {
                maxBusinessTokenError.value = 'Введите токен MAX Business'
                return
            }
            maxBusinessTokenLoading.value = true
            maxBusinessTokenError.value = ''
            try {
                const connectResponse = await api.networkFixer.connectMaxBusiness({
                    connectorId: props.connector.connectorId,
                    token,
                })
                if (connectResponse?.data?.error) {
                    const message = connectResponse.data.data || 'Ошибка подключения MAX Business'
                    maxBusinessTokenError.value = message
                    return
                }
                await checkAndHandleWaStatus()
            } finally {
                maxBusinessTokenLoading.value = false
            }
        }

        const connectSmsGateByCredentials = async () => {
            const username = smsGateUsername.value?.trim()
            const password = smsGatePassword.value?.trim()
            const deviceId = smsGateDeviceId.value?.trim()
            const phoneNumber = smsGatePhone.value?.trim()
            if (!username || !password || !deviceId || !phoneNumber) {
                smsGateCredentialsError.value = 'Заполните имя пользователя, пароль, ID устройства и номер телефона'
                return
            }
            if (!isValidPhone(phoneNumber)) {
                smsGateCredentialsError.value = 'Введите корректный номер телефона'
                return
            }
            smsGateCredentialsLoading.value = true
            smsGateCredentialsError.value = ''
            try {
                const connectResponse = await api.networkFixer.connectSmsGate({
                    connectorId: props.connector.connectorId,
                    username,
                    password,
                    deviceId,
                    phone: phoneNumber,
                })
                if (connectResponse?.data?.error) {
                    const message = connectResponse.data.data || 'Ошибка подключения SMS'
                    smsGateCredentialsError.value = message
                    return
                }
                const connectedPhone =
                    connectResponse?.data?.data?.phone ||
                    phoneNumber.replace(/[^\d+]/g, '')
                if (connectedPhone) {
                    phone.value = connectedPhone
                    const currentConnector =
                        root.$store.getters['connectorById'](props.connector.connectorId) ||
                        props.connector
                    root.$store.commit('updateConnector', {
                        ...currentConnector,
                        phone: connectedPhone,
                        alive: true,
                    })
                    emit('input', Object.assign({}, props.value, { phone: connectedPhone }))
                    phoneKey.value++
                }
                await checkAndHandleWaStatus()
            } finally {
                smsGateCredentialsLoading.value = false
            }
        }

        // true — статус обработан веткой переезда; false — переезд к каналу не относится.
        // Старые QR-ручки для флагового канала не зовём: они переписывают канал на себя.
        // alive и auth-status-changed не трогаем — канал пока работает через старого провайдера.
        const checkDhMigrationStatus = async (): Promise<boolean> => {
            const result = await fetchDhMigrationQr(props.connector.connectorId)
            if (result.status === 'fallback') {
                dhMigrationFallback.value = true
                return false
            }
            if (result.status === 'authorized') {
                qr.value.data = { type: 'alreadyLogged', status: 'authorized' }
                unAuthInProcess.value = false
            } else if (result.status === 'ready') {
                qr.value.data = { type: 'qrCode', status: 'ready' }
                unAuthInProcess.value = false
                await root.$nextTick()
                const qrCodeImage = root.$refs.qrCode as any
                if (qrCodeImage) {
                    qrCodeImage.setAttribute('src', result.imageSrc)
                }
            } else if (result.status === 'error') {
                // Для пользователя всё выглядит как обычная загрузка QR.
                console.error('dh-migration QR error:', result.message)
                qr.value.data = { type: 'waiting', status: null }
            } else {
                qr.value.data = { type: 'waiting', status: null }
            }
            return true
        }

        /**
         * Канал уже переехал на наш провайдер, а в сторе у страницы остался старый —
         * бэк отвечает `provider_changed` вместо того, чтобы тянуть канал назад к стороннему.
         * Обновляем коннектор, и следующий опрос уйдёт в ручку нашего провайдера.
         * До 07.10 старая страница так дёргала канал туда-сюда весь день.
         */
        const applyProviderChanged = (providerType: string) => {
            const currentConnector =
                root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
            root.$store.commit('updateConnector', { ...currentConnector, providerType })
            qr.value.data = { type: 'waiting', status: null }
            qr.value.loading = false
        }

        const checkAndHandleWaStatus = async () => {
            // Не выполняем запросы, если идет смена провайдера
            if (providerChanging.value) {
                return
            }
            
            qr.value.loading = true

            if (isDhMigration.value) {
                try {
                    if (await checkDhMigrationStatus()) {
                        qr.value.loading = false
                        return
                    }
                } catch (e) {
                    console.error('Ошибка при получении QR переезда:', e)
                    qr.value.loading = false
                    return
                }
            }
            
            // Для провайдера Data Heroes (whatsapp) используем специальные API запросы
            if (isDataHeroesProvider.value) {
                try {
                    // Проверяем статус коннектора
                    const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                    
                    // Проверяем телефон из коннектора (приоритет полю phone коннектора)
                    const connectorPhone = currentConnector?.phone || props.connector?.phone
                    if (connectorPhone && !phone.value) {
                        phone.value = connectorPhone
                        emit('input', Object.assign({}, props.value, { phone: connectorPhone }))
                        phoneKey.value++
                    }
                    
                    if (currentConnector?.alive) {
                        // Если коннектор уже авторизован
                        qr.value.data = { type: 'alreadyLogged', status: 'authorized' }
                        emitAuthStatusChanged(true)
                        // Сбрасываем таймер, так как авторизация завершена
                        lastDataHeroesQrRequestTime.value = null
                    } else {
                        if (lastDataHeroesQrRequestTime.value) {
                            qr.value.loading = false
                            return
                        }
                        
                        // Единожды вызываем запрос
                        lastDataHeroesQrRequestTime.value = true
                        
                        // Если не авторизован, получаем QR-код
                        const qrResponse = await getQrCode({
                            projectId: props.connector.projectId,
                            connectorId: props.connector.connectorId,
                        })
                        
                        if (qrResponse.error) {
                            console.error('Ошибка при получении QR-кода:', qrResponse.error)
                            qr.value.loading = false
                            return
                        }
                        
                        if (qrResponse.data?.QR) {
                            // Устанавливаем тип QR-кода сначала, чтобы элемент отобразился
                            qr.value.data = { type: 'qrCode' }
                            rememberWhatsappQrShown()
                            unAuthInProcess.value = false
                            
                            // Ждем следующий тик, чтобы элемент был в DOM
                            await root.$nextTick()
                            
                            // Конвертируем QR-код в base64 изображение для отображения
                            const canvas = document.createElement('canvas')
                            await QRCode.toCanvas(canvas, qrResponse.data.QR)
                            const qrCodeImage = root.$refs.qrCode as any
                            if (qrCodeImage) {
                                qrCodeImage.setAttribute('src', canvas.toDataURL('image/png'))
                            }
                        }
                    }
                } catch (e) {
                    console.error('Ошибка при обработке Data Heroes:', e)
                }
                qr.value.loading = false
                return
            }

            // Определяем bot provider на основе providerType
            const botProvider = getBotProviderFromProviderType(props.providerType)
            
            if (!botProvider) {
                qr.value.loading = false
                return
            }

            // Для telegramApi (wApi-telegram) - получаем QR-код
            if (botProvider === 'wApi-telegram') {
                // Если выбран метод ввода кода, пропускаем получение QR-кода
                if (telegramAuthMethod.value === 'code') {
                    qr.value.loading = false
                    return
                }
                
                const now = Date.now()
                // Проверяем, прошло ли более 10 секунд с последнего запроса
                if (lastTgQrRequestTime.value !== null && (now - lastTgQrRequestTime.value) < 10000) {
                    qr.value.loading = false
                    return
                }
                
                const response = await api.networkFixer
                    .getTgWaQrRework({ connectorId: props.connector.connectorId })
                
                // Сохраняем время текущего запроса ПОСЛЕ выполнения запроса
                lastTgQrRequestTime.value = Date.now()
                
                qr.value.data = response.data ?? {}
                
                // Проверяем различные варианты ответа, указывающие на авторизацию
                const isAuthorizedInResponse = qr.value.data?.type === 'alreadyLogged' || 
                    qr.value.data?.status === 'authorized' ||
                    qr.value.data === 'authorized' ||
                    response.data?.authorized === true ||
                    qr.value.data?.data === 'Instance already authorized' ||
                    response.data?.data === 'Instance already authorized'
                
                if (isAuthorizedInResponse) {
                    qr.value.data = { type: 'alreadyLogged', status: 'authorized' }
                    emitAuthStatusChanged(true)
                    unAuthInProcess.value = false
                    lastTgQrRequestTime.value = null // Сбрасываем таймер, так как авторизация завершена
                } else if (qr.value.data?.data?.qr) {
                    // Если есть QR-код, устанавливаем type и отображаем изображение
                    qr.value.data.type = 'qrCode'
                    const qrCodeImage = root.$refs.qrCode as any
                    if (qrCodeImage) {
                        qrCodeImage.setAttribute('src', 'data:image/jpg;base64,' + qr.value.data.data.qr)
                        unAuthInProcess.value = false
                    }
                } else {
                    // Если в коннекторе alive: true, но API говорит, что требуется QR-код, обновляем store
                    const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                    if (currentConnector?.alive === true) {
                        const updatedConnector = {
                            ...currentConnector,
                            alive: false
                        }
                        root.$store.commit('updateConnector', updatedConnector)
                    }
                }
            }

            if (botProvider === 'dh-telegram') {
                // В режиме ввода кода QR не запрашиваем (авторизация идёт через send-code/sign-in)
                if (telegramAuthMethod.value === 'code') {
                    qr.value.loading = false
                    return
                }
                // Уже на шаге 2FA (после сканирования QR) — не перезапрашиваем QR: клиент
                // в tg-service держит pending-состояние, нужен только ввод пароля.
                if (tgSendCode.value.passwordMode) {
                    qr.value.loading = false
                    return
                }
                const response = await api.networkFixer
                    .getTgDhChannelQr({ connectorId: props.connector.connectorId })

                const status = response.data?.status ?? null

                if (status === 'authorized') {
                    tgSendCode.value.passwordMode = false
                    qr.value.data = { type: 'alreadyLogged', status: 'authorized' }
                    emitAuthStatusChanged(true)
                    unAuthInProcess.value = false
                    if (!tgYmGoalsSent.value.authorized) {
                        logTgConnectionStage('authorized', { connectorId: props.connector.connectorId })
                        tgYmGoalsSent.value.authorized = true
                    }
                } else if (status === 'ready') {
                    const rawQr = response.data?.qr ?? response.data?.data?.qr
                    qr.value.data = { type: 'qrCode', status: 'ready', qr: rawQr }
                    const qrCodeImage = root.$refs.qrCode as any
                    if (qrCodeImage && rawQr) {
                        qrCodeImage.setAttribute('src', 'data:image/png;base64,' + rawQr)
                        unAuthInProcess.value = false
                    }
                    if (!tgYmGoalsSent.value.qrShown) {
                        logTgConnectionStage('qr_shown', { connectorId: props.connector.connectorId })
                        tgYmGoalsSent.value.qrShown = true
                    }
                } else if (status === 'password_needed') {
                    // 2FA: QR принят, дальше нужен облачный пароль — показываем тот же шаг ввода.
                    tgSendCode.value.passwordMode = true
                    tgSendCode.value.error = null
                    qr.value.data = { type: 'waiting', status: 'password_needed' }
                    if (!tgYmGoalsSent.value.twoFa) {
                        logTgConnectionStage('2fa_needed', { connectorId: props.connector.connectorId })
                        tgYmGoalsSent.value.twoFa = true
                    }
                } else {
                    qr.value.data = { type: 'waiting', status }
                }
            }

            // Для whatsappBasis (basis-api)
            if (botProvider === 'basis-api') {
                const response = await api.networkFixer
                    .getAppQrRework({ connectorId: props.connector.connectorId })
                if (response.data?.status === 'provider_changed') {
                    applyProviderChanged(response.data.providerType)
                    return
                }
                qr.value.data = response.data ?? {}
                
                // Проверяем различные варианты ответа, указывающие на авторизацию
                const isAuthorizedInResponse = qr.value.data?.type === 'alreadyLogged' || 
                    qr.value.data?.status === 'authorized' ||
                    qr.value.data === 'authorized' ||
                    response.data?.authorized === true
                
                if (isAuthorizedInResponse) {
                    qr.value.data = { type: 'alreadyLogged', status: 'authorized' }
                    emitAuthStatusChanged(true)
                } else {
                    // Если в коннекторе alive: true, но API говорит, что требуется QR-код, обновляем store
                    const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                    if (currentConnector?.alive === true) {
                        const updatedConnector = {
                            ...currentConnector,
                            alive: false
                        }
                        root.$store.commit('updateConnector', updatedConnector)
                    }
                    
                    if (qr.value.data?.type === 'qrCode') {
                        rememberWhatsappQrShown()
                        const qrCodeImage = root.$refs.qrCode as any
                        if (qrCodeImage) {
                            qrCodeImage.setAttribute('src', 'data:image/jpg;base64,' + qr.value.data?.message)
                            unAuthInProcess.value = false
                        }
                    }
                }
            }

            // Для whatsappApi (wApi-whatsapp)
            if (botProvider === 'wApi-whatsapp') {
                const now = Date.now()
                // Проверяем, прошло ли более 10 секунд с последнего запроса
                if (lastWa2QrRequestTime.value !== null && (now - lastWa2QrRequestTime.value) < 10000) {
                    qr.value.loading = false
                    return
                }
                
                const response = await api.networkFixer
                    .getAppQrWApiRework({ connectorId: props.connector.connectorId })
                
                // Сохраняем время текущего запроса ПОСЛЕ выполнения запроса
                lastWa2QrRequestTime.value = Date.now()
                
                qr.value.data = response.data ?? { type: 'waiting' }
                
                // Проверяем различные варианты ответа, указывающие на авторизацию
                const isAuthorizedInResponse = qr.value.data?.type === 'alreadyLogged' || 
                    qr.value.data?.status === 'authorized' ||
                    qr.value.data === 'authorized' ||
                    response.data?.authorized === true
                
                if (isAuthorizedInResponse) {
                    qr.value.data = { type: 'alreadyLogged', status: 'authorized' }
                    emitAuthStatusChanged(true)
                    lastWa2QrRequestTime.value = null // Сбрасываем таймер, так как авторизация завершена
                } else {
                    // Если в коннекторе alive: true, но API говорит, что требуется QR-код, обновляем store
                    const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                    if (currentConnector?.alive === true) {
                        const updatedConnector = {
                            ...currentConnector,
                            alive: false
                        }
                        root.$store.commit('updateConnector', updatedConnector)
                    }
                    
                    if (qr.value.data?.type === 'qrcode') {
                        qr.value.data.type = 'qrCode'
                    }
                    if (qr.value.data?.type === 'qrCode') {
                        rememberWhatsappQrShown()
                        const qrCodeImage = root.$refs.qrCode as any
                        if (qrCodeImage) {
                            qrCodeImage.setAttribute('src', qr.value.data?.message)
                            unAuthInProcess.value = false
                        }
                    }
                }
            }

            // Для maxBasis (green-api-max) — авторизация по QR-коду, как для других ботов
            if (botProvider === 'green-api-max') {
                const now = Date.now()
                // Проверяем, прошло ли более 15 секунд с последнего запроса
                if (lastMaxQrRequestTime.value !== null && (now - lastMaxQrRequestTime.value) < 15000) {
                    qr.value.loading = false
                    return
                }
                
                const response = await api.networkFixer
                    .getMaxQrRework({ connectorId: props.connector.connectorId })
                
                // Сохраняем время текущего запроса ПОСЛЕ выполнения запроса
                lastMaxQrRequestTime.value = Date.now()
                
                if (response.data?.status === 'provider_changed') {
                    applyProviderChanged(response.data.providerType)
                    return
                }

                qr.value.data = response.data ?? {}
                
                // Проверяем различные варианты ответа, указывающие на авторизацию
                const isAuthorizedInResponse = qr.value.data?.type === 'alreadyLogged' || 
                    qr.value.data?.status === 'authorized' ||
                    qr.value.data === 'authorized' ||
                    response.data?.authorized === true
                
                if (isAuthorizedInResponse) {
                    qr.value.data = { type: 'alreadyLogged', status: 'authorized' }
                    emitAuthStatusChanged(true)
                    lastMaxQrRequestTime.value = null // Сбрасываем таймер, так как авторизация завершена
                } else {
                    // Если в коннекторе alive: true, но API говорит, что требуется QR-код, обновляем store
                    const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                    if (currentConnector?.alive === true) {
                        const updatedConnector = {
                            ...currentConnector,
                            alive: false
                        }
                        root.$store.commit('updateConnector', updatedConnector)
                    }
                    
                    if (qr.value.data?.type === 'qrCode') {
                        const qrCodeImage = root.$refs.qrCode as any
                        if (qrCodeImage) {
                            qrCodeImage.setAttribute('src', 'data:image/jpg;base64,' + qr.value.data?.message)
                            unAuthInProcess.value = false
                        }
                    }
                }
            }

            if (botProvider === 'dh-max') {
                const response = await api.networkFixer
                    .getDhMaxChannelQr({ connectorId: props.connector.connectorId })

                const status = response.data?.status ?? null

                if (status === 'authorized') {
                    qr.value.data = { type: 'alreadyLogged', status: 'authorized' }
                    emitAuthStatusChanged(true)
                    unAuthInProcess.value = false
                } else if (status === 'ready') {
                    const rawQr = response.data?.qr ?? response.data?.data?.qr
                    qr.value.data = { type: 'qrCode', status: 'ready', qr: rawQr }
                    const qrCodeImage = root.$refs.qrCode as any
                    if (qrCodeImage && rawQr) {
                        qrCodeImage.setAttribute('src', 'data:image/png;base64,' + rawQr)
                        unAuthInProcess.value = false
                    }
                } else {
                    qr.value.data = { type: 'waiting', status }
                }
            }

            if (botProvider === 'dh-whatsapp') {
                const response = await api.networkFixer
                    .getWaDhChannelQr({ connectorId: props.connector.connectorId })

                const status = response.data?.status ?? null

                if (status === 'authorized') {
                    qr.value.data = { type: 'alreadyLogged', status: 'authorized' }
                    emitAuthStatusChanged(true)
                    unAuthInProcess.value = false
                } else if (status === 'ready') {
                    // wa-service отдаёт QR СЫРОЙ строкой Baileys (не base64 PNG). Рендерим
                    // по DataHeroes-паттерну: QRCode.toCanvas(rawQr) → toDataURL → <img ref="qrCode">.
                    const rawQr = response.data?.qr ?? response.data?.data?.qr
                    qr.value.data = { type: 'qrCode', status: 'ready', qr: rawQr }
                    if (rawQr) {
                        await root.$nextTick()
                        const qrCodeImage = root.$refs.qrCode as any
                        if (qrCodeImage) {
                            const canvas = document.createElement('canvas')
                            await QRCode.toCanvas(canvas, rawQr)
                            qrCodeImage.setAttribute('src', canvas.toDataURL('image/png'))
                            unAuthInProcess.value = false
                        }
                    }
                } else {
                    qr.value.data = { type: 'waiting', status }
                }
            }

            if (botProvider === 'max-business') {
                const statusResponse = await api.networkFixer.getMaxBusinessStatus({
                    connectorId: props.connector.connectorId,
                })
                const authorized =
                    statusResponse?.data?.error === false &&
                    statusResponse?.data?.data === true

                qr.value.data = authorized
                    ? { type: 'alreadyLogged', status: 'authorized' }
                    : { type: 'notAuthorized', status: 'notAuthorized' }
                emitAuthStatusChanged(authorized)
            }

            if (botProvider === 'sms-gate') {
                const statusResponse = await api.networkFixer.getSmsGateStatus({
                    connectorId: props.connector.connectorId,
                })
                const authorized =
                    statusResponse?.data?.error === false &&
                    statusResponse?.data?.data === true

                qr.value.data = authorized
                    ? { type: 'alreadyLogged', status: 'authorized' }
                    : { type: 'notAuthorized', status: 'notAuthorized' }
                emitAuthStatusChanged(authorized)

                // Номер SIM берём из канала (его отдаёт та же ручка статуса), а не из
                // локального состояния: тестовую отправку показываем только когда номер
                // действительно сохранён на бэке.
                const channelPhone = statusResponse?.data?.phone || ''
                smsGateOnline.value = statusResponse?.data?.online ?? null
                smsGateLastSeen.value = statusResponse?.data?.lastSeen || ''
                smsGateDeviceName.value = statusResponse?.data?.deviceName || ''
                const nextSimCards = Array.isArray(statusResponse?.data?.simCards)
                    ? statusResponse.data.simCards
                    : []
                smsGateSimCards.value = nextSimCards
                if (!smsGateSimSaving.value) {
                    const apiSim = statusResponse?.data?.simNumber
                    const apiNumber = apiSim == null || apiSim === '' ? null : Number(apiSim)
                    const listed = (value) =>
                        value != null &&
                        value !== '' &&
                        nextSimCards.some((sim) => Number(sim.simNumber) === Number(value))
                    if (listed(apiNumber)) {
                        smsGateSimNumber.value = apiNumber
                    } else if (nextSimCards.length > 1 && !listed(smsGateSimNumber.value)) {
                        const first = Number(nextSimCards[0].simNumber)
                        if (Number.isFinite(first)) void selectSmsGateSim(first)
                    } else if (apiNumber != null && nextSimCards.length <= 1) {
                        smsGateSimNumber.value = apiNumber
                    }
                }
                smsGateSimPhones.value = statusResponse?.data?.simPhones || {}
                smsGateChannelPhone.value = channelPhone
                smsGateStatusLoaded.value = true
                if (channelPhone && channelPhone !== phone.value) {
                    phone.value = channelPhone
                    phoneKey.value++
                }
            }

            // Проверяем, нужно ли запустить опрос телефона
            if (isAuthorized.value && !phone.value && !unAuthInProcess.value && !isMaxBusinessProvider.value && !isSmsGateProvider.value) {
                startPhonePolling()
            }

            const ref: any = root.$refs.phoneValue
            if(ref && phone.value && ref.currentPhone.replace(/[^\d+]/g, '') !== phone.value) {
                phoneKey.value++
            }
            qr.value.loading = false
        }

        const setFetchQrInterval = async () => {
            if (props.value.disabled || providerChanging.value) {
                return
            }
            if (props.appCreated) {
                checkAndHandleWaStatus()
            }
            fetchQrInterval.value = setInterval(async () => {
                if (props.appCreated && !providerChanging.value) {
                    checkAndHandleWaStatus()
                }
            }, 5 * 1000)
        }

        // Хранилище состояния выделения для каждого поля
        const selectionStates = new Map<number, { start: number; end: number }>()
        
        // Обработка фокуса для сохранения состояния выделения
        const handleCodeFocus = (index: number, event: Event) => {
            const target = event.target as HTMLInputElement
            // Сохраняем состояние выделения при получении фокуса
            const selection = {
                start: target.selectionStart || 0,
                end: target.selectionEnd || 0
            }
            selectionStates.set(index, selection)
        }
        
        // Обработка beforeinput для корректного удаления и сохранения состояния выделения
        const handleCodeBeforeInput = (index: number, event: Event) => {
            const target = event.target as HTMLInputElement
            const inputEvent = event as InputEvent
            const inputType = inputEvent.inputType
            
            // Сохраняем состояние выделения ДО ввода
            const selectionStart = target.selectionStart || 0
            const selectionEnd = target.selectionEnd || 0
            const selection = {
                start: selectionStart,
                end: selectionEnd
            }
            selectionStates.set(index, selection)
            
            // Если это ввод текста в заполненное поле без выделения - обрабатываем вручную
            if ((inputType === 'insertText' || inputType === 'insertCompositionText') && 
                codeDigits.value[index] && 
                selectionStart === selectionEnd) {
                // Сбрасываем ошибку при начале нового ввода
                if (tgSendCode.value.error) {
                    tgSendCode.value.error = null
                }
                // Браузер блокирует ввод из-за maxlength="1", поэтому обрабатываем вручную
                const newChar = inputEvent.data
                if (newChar && /[0-9]/.test(newChar)) {
                    inputEvent.preventDefault()
                    // Обрабатываем ввод вручную
                    const oldDigit = codeDigits.value[index]
                    codeDigits.value[index] = newChar
                    
                    // Сдвигаем остальные символы вправо
                    let shiftValue = oldDigit
                    for (let i = index + 1; i < 5; i++) {
                        const temp = codeDigits.value[i]
                        codeDigits.value[i] = shiftValue
                        shiftValue = temp
                        if (!shiftValue) break
                    }
                    
                    // Синхронизируем с tgSendCode.code
                    tgSendCode.value.code = codeDigits.value.join('')
                    
                    // Обновляем значение в поле и переходим к следующему полю (index + 1)
                    root.$nextTick(() => {
                        target.value = codeDigits.value[index] || ''
                        
                        // Переходим к следующему полю после сдвига (index + 1)
                        if (index + 1 < 5) {
                            const nextInput = root.$refs[`codeInput${index + 1}`] as HTMLInputElement[]
                            if (nextInput && nextInput[0]) {
                                nextInput[0].focus()
                            }
                        }
                    })
                    return false
                }
            }
            
            // Если это удаление (deleteContentBackward или deleteContentForward)
            if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') {
                if (codeDigits.value[index]) {
                    // Если поле заполнено, разрешаем удаление
                    return true
                } else if (index > 0 && inputType === 'deleteContentBackward') {
                    // Если поле пустое и это Backspace, переходим к предыдущему полю
                    inputEvent.preventDefault()
                    codeDigits.value[index - 1] = ''
                    tgSendCode.value.code = codeDigits.value.join('')
                    root.$nextTick(() => {
                        const prevInput = root.$refs[`codeInput${index - 1}`] as HTMLInputElement[]
                        if (prevInput && prevInput[0]) {
                            prevInput[0].focus()
                        }
                    })
                    return false
                }
            }
            return true
        }
        
        // Обработка ввода в отдельные поля кода
        const handleCodeInput = (index: number, event: Event) => {
            // Сбрасываем ошибку при начале нового ввода
            if (tgSendCode.value.error) {
                tgSendCode.value.error = null
            }
            
            const target = event.target as HTMLInputElement
            const inputValue = target.value.replace(/[^0-9]/g, '')
            // Сохраняем старое значение из массива ДО обработки (браузер мог уже изменить target.value)
            const oldDigit = codeDigits.value[index]
            const wasFilled = oldDigit !== ''
            
            // Получаем сохраненное состояние выделения ДО ввода
            const savedSelection = selectionStates.get(index)
            // Если нет сохраненного состояния, считаем что текст не выделен (по умолчанию)
            const isTextSelected = savedSelection ? savedSelection.start !== savedSelection.end : false
            
            if (inputValue.length > 1) {
                // Если вставлено несколько символов, распределяем по полям
                const digits = inputValue.slice(0, 5).split('')
                digits.forEach((digit, i) => {
                    if (i < 5) {
                        codeDigits.value[i] = digit
                    }
                })
                // Фокусируемся на последнем заполненном поле
                const lastFilledIndex = Math.min(digits.length - 1, 4)
                root.$nextTick(() => {
                    const nextInput = root.$refs[`codeInput${lastFilledIndex}`] as HTMLInputElement[]
                    if (nextInput && nextInput[0]) {
                        nextInput[0].focus()
                    }
                })
            } else if (inputValue === '') {
                // Удаление символа - просто очищаем поле
                codeDigits.value[index] = ''
            } else {
                // Ввод новой цифры
                const newDigit = inputValue[inputValue.length - 1]
                
                if (isTextSelected) {
                    // Если текст был выделен - просто заменяем (стандартное поведение браузера)
                    codeDigits.value[index] = newDigit
                    // Переходим к следующему полю
                    if (newDigit && index < 4) {
                        root.$nextTick(() => {
                            const nextInput = root.$refs[`codeInput${index + 1}`] as HTMLInputElement[]
                            if (nextInput && nextInput[0]) {
                                nextInput[0].focus()
                            }
                        })
                    }
                } else if (wasFilled && !isTextSelected) {
                    // Если поле было заполнено, текст НЕ выделен - сдвигаем все вправо
                    // Браузер уже заменил символ в поле из-за maxlength="1", но мы сдвинем старый символ вправо
                    codeDigits.value[index] = newDigit
                    
                    // Сдвигаем остальные символы вправо, начиная со следующего поля
                    let shiftValue = oldDigit
                    for (let i = index + 1; i < 5; i++) {
                        const temp = codeDigits.value[i]
                        codeDigits.value[i] = shiftValue
                        shiftValue = temp
                        if (!shiftValue) break
                    }
                    
                    // Переходим к следующему пустому полю после сдвига
                    let nextEmptyIndex = index + 1
                    while (nextEmptyIndex < 5 && codeDigits.value[nextEmptyIndex]) {
                        nextEmptyIndex++
                    }
                    if (nextEmptyIndex < 5) {
                        root.$nextTick(() => {
                            const nextInput = root.$refs[`codeInput${nextEmptyIndex}`] as HTMLInputElement[]
                            if (nextInput && nextInput[0]) {
                                nextInput[0].focus()
                            }
                        })
                    }
                } else if (!wasFilled) {
                    // Обычный ввод в пустое поле
                    codeDigits.value[index] = newDigit
                    
                    // Переходим к следующему полю, если введена цифра
                    if (newDigit && index < 4) {
                        root.$nextTick(() => {
                            const nextInput = root.$refs[`codeInput${index + 1}`] as HTMLInputElement[]
                            if (nextInput && nextInput[0]) {
                                nextInput[0].focus()
                            }
                        })
                    }
                } else {
                    // Поле было заполнено и введен тот же символ - просто обновляем
                    codeDigits.value[index] = newDigit
                }
            }
            
            // Синхронизируем с tgSendCode.code
            tgSendCode.value.code = codeDigits.value.join('')
            
            // Принудительно обновляем значение в поле для корректной работы
            root.$nextTick(() => {
                target.value = codeDigits.value[index] || ''
            })
        }
        
        // Обработка нажатий клавиш
        const handleCodeKeydown = (index: number, event: KeyboardEvent) => {
            const target = event.target as HTMLInputElement
            
            if (event.key === 'Backspace') {
                const currentValue = codeDigits.value[index]
                const selectionStart = target.selectionStart || 0
                
                if (currentValue && selectionStart === 0) {
                    // Если курсор в начале и поле заполнено, очищаем поле и переходим к предыдущему
                    event.preventDefault()
                    codeDigits.value[index] = ''
                    tgSendCode.value.code = codeDigits.value.join('')
                    if (index > 0) {
                        const prevInput = root.$refs[`codeInput${index - 1}`] as HTMLInputElement[]
                        if (prevInput && prevInput[0]) {
                            prevInput[0].focus()
                        }
                    }
                } else if (!currentValue && index > 0) {
                    // Если поле пустое и нажат Backspace, переходим к предыдущему полю и очищаем его
                    event.preventDefault()
                    codeDigits.value[index - 1] = ''
                    tgSendCode.value.code = codeDigits.value.join('')
                    const prevInput = root.$refs[`codeInput${index - 1}`] as HTMLInputElement[]
                    if (prevInput && prevInput[0]) {
                        prevInput[0].focus()
                    }
                }
                // Если поле заполнено и курсор не в начале, разрешаем стандартное удаление
            } else if (event.key === 'Delete') {
                // При Delete всегда очищаем текущее поле
                if (codeDigits.value[index]) {
                    event.preventDefault()
                    codeDigits.value[index] = ''
                    tgSendCode.value.code = codeDigits.value.join('')
                }
            } else if (event.key === 'ArrowLeft' && index > 0) {
                event.preventDefault()
                const prevInput = root.$refs[`codeInput${index - 1}`] as HTMLInputElement[]
                if (prevInput && prevInput[0]) {
                    prevInput[0].focus()
                }
            } else if (event.key === 'ArrowRight' && index < 4) {
                event.preventDefault()
                const nextInput = root.$refs[`codeInput${index + 1}`] as HTMLInputElement[]
                if (nextInput && nextInput[0]) {
                    nextInput[0].focus()
                }
            }
        }
        
        // Обработка вставки
        const handleCodePaste = (event: ClipboardEvent) => {
            event.preventDefault()
            const pastedData = event.clipboardData?.getData('text') || ''
            const digits = pastedData.replace(/[^0-9]/g, '').slice(0, 5).split('')
            
            digits.forEach((digit, i) => {
                if (i < 5) {
                    codeDigits.value[i] = digit
                }
            })
            
            tgSendCode.value.code = codeDigits.value.join('')
            
            // Фокусируемся на последнем заполненном поле
            const lastFilledIndex = Math.min(digits.length - 1, 4)
            const nextInput = root.$refs[`codeInput${lastFilledIndex}`] as HTMLInputElement[]
            if (nextInput && nextInput[0]) {
                nextInput[0].focus()
            }
        }

        const sendCode = async () => {
            if(selectedBotProvider.value === 'wApi-telegram') {
                // Очищаем ошибку при нажатии на кнопку "Получить код"
                tgSendCode.value.error = null
                tg1CodeWaiting.value = true
                try {
                    qr.value.data = { type: 'qrCode' }
                    const instance = await api.networkFixer.createInstanceTgRework({
                        connectorId: props.connector.connectorId,
                    })
                    if (instance.error) {
                        tgSendCode.value.error = 'Ошибка при получении кода, попробуйте еще раз'
                        return
                    }
                    const data = await api.networkFixer.sendCodeTgRework({
                        connectorId: props.connector.connectorId,
                        phone: tgSendCode.value.phone,
                    })
                    if(data?.data?.error) {
                        if (data?.data?.data?.includes('[420 FLOOD_WAIT_X]')) {
                            tgSendCode.value.error = 'Много попыток, попробуйте позже'
                        } else if (data?.data?.data?.includes('[400 PHONE_NUMBER_INVALID]')) {
                            tgSendCode.value.error = 'Некорректный номер телефона'
                        } else {
                            tgSendCode.value.error = 'Ошибка при отправке кода, попробуйте еще раз'
                        }
                        return
                    } else if (data?.error) {
                        tgSendCode.value.error = 'Ошибка при отправке кода, попробуйте еще раз'
                        return
                    }
                    unAuthInProcess.value = false
                    tgSendCode.value.code = ''
                    codeDigits.value = ['', '', '', '', '']
                    tgSendCode.value.isSendCode = true
                    tgSendCode.value.showSendBtn = true
                    // Свежий код — сбрасываем возможный прежний шаг 2FA.
                    tgSendCode.value.passwordMode = false
                    tgSendCode.value.password = ''
                } catch (e) {
                    console.error(e)
                    tgSendCode.value.error = 'Ошибка при получении кода, попробуйте еще раз'
                } finally {
                    tg1CodeWaiting.value = false
                }
            } else if (selectedBotProvider.value === 'dh-telegram') {
                tgSendCode.value.error = null
                tg1CodeWaiting.value = true
                try {
                    const res = await api.networkFixer.sendCodeTgDhChannel({
                        connectorId: props.connector.connectorId,
                        phone: tgSendCode.value.phone,
                    })
                    if (res?.error) {
                        const e = String(res.error)
                        if (e.includes('FLOOD_WAIT')) {
                            tgSendCode.value.error = 'Много попыток, попробуйте позже'
                        } else if (e.includes('PHONE_NUMBER_INVALID')) {
                            tgSendCode.value.error = 'Некорректный номер телефона'
                        } else {
                            tgSendCode.value.error = 'Ошибка при отправке кода, попробуйте еще раз'
                        }
                        return
                    }
                    unAuthInProcess.value = false
                    tgSendCode.value.code = ''
                    codeDigits.value = ['', '', '', '', '']
                    tgSendCode.value.isSendCode = true
                    tgSendCode.value.showSendBtn = true
                    // Свежий код — сбрасываем возможный прежний шаг 2FA.
                    tgSendCode.value.passwordMode = false
                    tgSendCode.value.password = ''
                } catch (e) {
                    console.error(e)
                    tgSendCode.value.error = 'Ошибка при получении кода, попробуйте еще раз'
                } finally {
                    tg1CodeWaiting.value = false
                }
            } else {
                try {
                    maxSendCode.value.sendReqInProcess = true
                    maxSendCode.value.currentError = null
                    const res = await api.networkFixer.getAuthorizationCodeGreenApiMaxRework({ phone: maxSendCode.value.phone, connectorId: props.connector.connectorId })
                    maxSendCode.value.sendReqInProcess = false
                    if (res.error) {
                        root.$store.dispatch('callNotify', 'Ошибка при получении кода (1)')
                        return
                    }
                    maxSendCode.value.isSendCode = true
                    if(res.data?.data?.status) {
                        maxSendCode.value.showSendBtn = true
                        maxSendCode.value.countdown = 60
                        unAuthInProcess.value = false
                        maxSendCode.value.code = ''
                    } else {
                        if (res.data?.data?.data?.reason === 'rate_limit_exceeded') {
                            maxSendCode.value.currentError = 'Слишком много запросов, повторите позже'
                            maxSendCode.value.countdown = Math.ceil(res.data?.data?.data?.retryAfter / 1000)
                        } else {
                            maxSendCode.value.currentError = 'Ошибка сервера, попробуйте позже'
                            maxSendCode.value.countdown = 30
                        }
                        return
                    }
                } catch (error) {
                    root.$store.dispatch('callNotify', 'Ошибка при получении кода (2)')
                    console.error(error)
                } finally {
                    maxSendCode.value.sendReqInProcess = false
                }
            }
            
        }

        const inputCode = async () => {
            if(selectedBotProvider.value === 'wApi-telegram') {
                let hasError = false
                try {
                    tg1CodeSending.value = true
                    const res = await api.networkFixer.inputCodeTgRework({
                        connectorId: props.connector.connectorId,
                        code: tgSendCode.value.code,
                    })
                    if(res?.data?.error) {
                        hasError = true
                        if (res?.data?.data?.includes('[400 PHONE_CODE_INVALID]')) {
                            tgSendCode.value.error = 'Неверный код, введите еще раз'
                        } else if (res?.data?.data?.includes('[400 PHONE_CODE_EXPIRED]')) {
                            tgSendCode.value.error = 'Истек срок действия кода, дождитесь возможности получить новый'
                        } else if (res?.data?.data?.includes('[420 FLOOD_WAIT_X]')) {
                            tgSendCode.value.error = 'Много попыток, попробуйте позже'
                        } else {
                            tgSendCode.value.error = 'Ошибка при отправке кода, попробуйте еще раз'
                        }
                    } else if (res?.error) {
                        hasError = true
                        tgSendCode.value.error = 'Ошибка при отправке кода, попробуйте еще раз'
                    } else {
                        qr.value.data = { type: 'alreadyLogged' }
                        const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                        root.$store.commit('updateConnector', {
                            ...currentConnector,
                            phone: tgSendCode.value.phone,
                        })
                        phone.value = tgSendCode.value.phone
                        emit('input', Object.assign({}, props.value, { phone: tgSendCode.value.phone }))
                        phoneKey.value++
                    }
                } catch (e) {
                    console.error(e)
                    hasError = true
                    tgSendCode.value.error = 'Ошибка при отправке кода, попробуйте еще раз'
                } finally {
                    tg1CodeSending.value = false
                    // Если произошла ошибка и блок ввода кода отображается — фокусируемся на первом поле
                    if (hasError && tgSendCode.value.isSendCode) {
                        root.$nextTick(() => {
                            const firstInput = root.$refs['codeInput0'] as HTMLInputElement[]
                            if (firstInput && firstInput[0]) {
                                firstInput[0].focus()
                            }
                        })
                    }
                }
            } else if (selectedBotProvider.value === 'dh-telegram') {
                let hasError = false
                try {
                    tg1CodeSending.value = true
                    const res = await api.networkFixer.signInTgDhChannel({
                        connectorId: props.connector.connectorId,
                        code: tgSendCode.value.code,
                    })
                    if (res?.error) {
                        hasError = true
                        const e = String(res.error)
                        if (e.includes('PHONE_CODE_INVALID')) {
                            tgSendCode.value.error = 'Неверный код, введите еще раз'
                        } else if (e.includes('PHONE_CODE_EXPIRED')) {
                            tgSendCode.value.error = 'Истек срок действия кода, получите новый'
                        } else if (e.includes('FLOOD_WAIT')) {
                            tgSendCode.value.error = 'Много попыток, попробуйте позже'
                        } else {
                            tgSendCode.value.error = 'Ошибка при отправке кода, попробуйте еще раз'
                        }
                    } else if (res?.data?.status === 'password_needed') {
                        // Аккаунт с 2FA: код принят, дальше — облачный пароль.
                        tgSendCode.value.passwordMode = true
                        tgSendCode.value.error = null
                        tgSendCode.value.password = ''
                    } else {
                        qr.value.data = { type: 'alreadyLogged' }
                        const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                        root.$store.commit('updateConnector', {
                            ...currentConnector,
                            phone: tgSendCode.value.phone,
                        })
                        phone.value = tgSendCode.value.phone
                        emit('input', Object.assign({}, props.value, { phone: tgSendCode.value.phone }))
                        phoneKey.value++
                    }
                } catch (e) {
                    console.error(e)
                    hasError = true
                    tgSendCode.value.error = 'Ошибка при отправке кода, попробуйте еще раз'
                } finally {
                    tg1CodeSending.value = false
                    if (hasError && tgSendCode.value.isSendCode) {
                        root.$nextTick(() => {
                            const firstInput = root.$refs['codeInput0'] as HTMLInputElement[]
                            if (firstInput && firstInput[0]) {
                                firstInput[0].focus()
                            }
                        })
                    }
                }
            } else {
                try {
                    maxSendCode.value.sendReqInProcess = true
                    maxSendCode.value.currentError = null
                    maxSendCode.value.sendCodeError = false
                    const res = await api.networkFixer.sendAuthorizationCodeGreenApiMaxRework({connectorId: props.connector.connectorId, code: maxSendCode.value.code.trim()})
                    maxSendCode.value.sendReqInProcess = false
                    if (res.error) {
                        root.$store.dispatch('callNotify', 'Ошибка при отправке кода (1)')
                        return
                    }
                    if(res.data?.data?.status) {
                        qr.value.data = { type: 'alreadyLogged' }
                        const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                        root.$store.commit('updateConnector', {
                            ...currentConnector,
                            phone: maxSendCode.value.phone,
                        })
                        phone.value = maxSendCode.value.phone
                        emit('input', Object.assign({}, props.value, { phone: maxSendCode.value.phone }))
                        phoneKey.value++
                    } else {
                        const reason = res.data?.data?.data?.reason
                        if(reason === 'verify_code_wrong') {
                            maxSendCode.value.currentError = 'Не верный код авторизации'
                        } else if (reason === 'code_expired') {
                            maxSendCode.value.currentError = 'Время действия кода подтверждения истекло'
                        } else {
                            maxSendCode.value.currentError = 'Ошибка сервера, попробуйте позже'
                        }
                        maxSendCode.value.sendCodeError = true
                        return
                    }
                } catch(error) {
                    root.$store.dispatch('callNotify', 'Ошибка при отправке кода (2)')
                    console.error(error)
                } finally {
                    maxSendCode.value.sendReqInProcess = false
                }
            }
        }

        // 2FA: подтверждение облачного пароля (после ответа password_needed на sign-in).
        const submitTgDhPassword = async () => {
            if (!tgSendCode.value.password || tg1CodeSending.value) return
            try {
                tg1CodeSending.value = true
                tgSendCode.value.error = null
                const res = await api.networkFixer.passwordTgDhChannel({
                    connectorId: props.connector.connectorId,
                    password: tgSendCode.value.password,
                })
                if (res?.error) {
                    const e = String(res.error)
                    if (e.includes('PASSWORD_HASH_INVALID')) {
                        tgSendCode.value.error = 'Неверный пароль, попробуйте ещё раз'
                    } else if (e.includes('FLOOD_WAIT')) {
                        tgSendCode.value.error = 'Много попыток, попробуйте позже'
                    } else {
                        tgSendCode.value.error = 'Ошибка при проверке пароля, попробуйте ещё раз'
                    }
                    return
                }
                // Успех — аккаунт авторизован.
                qr.value.data = { type: 'alreadyLogged' }
                tgSendCode.value.passwordMode = false
                tgSendCode.value.password = ''
                // Телефон проставляем только если он у нас есть (code-флоу). В QR-флоу его
                // не вводили — он придёт штатно через instance-state хук, не затираем пустым.
                if (tgSendCode.value.phone) {
                    const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                    root.$store.commit('updateConnector', {
                        ...currentConnector,
                        phone: tgSendCode.value.phone,
                    })
                    phone.value = tgSendCode.value.phone
                    emit('input', Object.assign({}, props.value, { phone: tgSendCode.value.phone }))
                    phoneKey.value++
                }
            } catch (e) {
                console.error(e)
                tgSendCode.value.error = 'Ошибка при проверке пароля, попробуйте ещё раз'
            } finally {
                tg1CodeSending.value = false
            }
        }

        const myRole = () => {
            // Используем уже рассчитанную роль проекта, чтобы избежать расхождений
            // между текущим пользователем и списком пользователей проекта
            return project.value?.myRole || 'observer'
        }

        const controlPhoneValidator = (phone: string) => {
            if (phone && !isValidPhone(phone)) {
                return 'Некорректный номер телефона'
            }
        }

        const controlOn = async () => {
            const phone = phoneToControl.value.replace('+', '')
            const checkRes = await api.networkFixer.checkPhoneBeforeActivatingControl({phone, connectorId: props.connector.connectorId})
            if (!checkRes?.error) {
                if (checkRes.data === false) {
                    isControlErrorShow.value = true
                    setTimeout(() => {
                        isControlErrorShow.value = false
                    }, 5000)
                } else {
                    const connectRes = await api.networkFixer.controlPhoneToggle({connectorId: props.connector.connectorId, phone})
                    if (!connectRes?.error) {
                        const listRes = await api.networkFixer.controlPhoneList({connectorId: props.connector.connectorId})
                        if (!listRes?.error) {
                            phoneToControlList.value = listRes?.data
                            phoneToControl.value = ''
                            phoneToControlKey.value++
                        } else {
                            root.$store.dispatch('callNotify', 'Произошла ошибка')
                        }
                    } else {
                        root.$store.dispatch('callNotify', 'Произошла ошибка')
                    }
                }
            } else {
                root.$store.dispatch('callNotify', 'Произошла ошибка')
            }
        }

        const controlOff = async (userId: string) => {
            const phone = userId.replace('+', '')
            const deConnectRes = await api.networkFixer.controlPhoneToggle({connectorId: props.connector.connectorId, phone})
            if (!deConnectRes?.error) {
                const listRes = await api.networkFixer.controlPhoneList({connectorId: props.connector.connectorId})
                if (!listRes?.error) {
                    phoneToControlList.value = listRes?.data
                } else {
                    root.$store.dispatch('callNotify', 'Произошла ошибка')
                }
            } else {
                root.$store.dispatch('callNotify', 'Произошла ошибка')
            }
        }

        const shareQrCode = async () => {
            const res = await api.networkFixer.getNoAuthToken({connectorId: props.connector.connectorId})
            let token: any
            if (!res.error) {
                token = res.data.token
                const isChannelsRework = project.value?.isChannelsRework ? 'true' : 'false'
                const qrCodeUrl = `${window.location.origin}/qr/${props.connector.connectorId}?token=${token}&isChannelsRework=${isChannelsRework}&provider=${selectedBotProvider.value}`
                await navigator.clipboard.writeText(qrCodeUrl)
                window.open(qrCodeUrl, '_blank')
                root.$store.dispatch('callNotify', 'Ссылка скопирована в буфер обмена')
            } else {
                root.$store.dispatch('callNotify', 'Ошибка при генерации ссылки')
            }
        }

        // Обработчик изменения метода авторизации Telegram
        const onTelegramAuthMethodChange = (method: 'code' | 'qr') => {
            // Сбрасываем состояние при смене метода
            if (method === 'qr') {
                tgSendCode.value = {
                    phone: '',
                    isSendCode: false,
                    countdown: 60,
                    condition: 'noAuth',
                    code: '',
                    showSendBtn: false,
                    error: null,
                    passwordMode: false,
                    password: '',
                    showPassword: false
                }
                codeDigits.value = ['', '', '', '', '']
                // Сбрасываем таймер при переключении на QR-метод
                lastTgQrRequestTime.value = null
            }
            qr.value.data = {}
        }

        // Проверка, авторизован ли канал
        const isAuthorized = computed(() => {
            return qr.value.data?.type === 'alreadyLogged' || 
                   qr.value.data === 'authorized' || 
                   qr.value.data?.status === 'authorized'
        })

        const emitAuthorizedDuringCreation = () => {
            if (props.mode !== 'create' || !props.inSidebar || unAuthInProcess.value) return
            if (!isAuthorized.value) return
            emit('authorized')
        }

        const emitAuthStatusChanged = (authorized: boolean) => {
            const connectorId = props.connector?.connectorId || props.connector?.id
            if (!connectorId) return
            if (lastEmittedAuthStatus.value === authorized) return
            lastEmittedAuthStatus.value = authorized
            emit('auth-status-changed', { connectorId, authorized })
        }

        const showChatBotStatisticsPromo = computed(() => {
            if (!props.inSidebar || props.isOnboarding || props.value?.disabled) return false
            if (slotBillingBannerMode.value === 'frozen') return false
            if (!isProjectChatBotConnector(props.connector)) return false
            if (!isAuthorized.value || unAuthInProcess.value) return false
            return true
        })

        const canOpenChatBotStatisticsReport = computed(() => {
            const projectId = props.connector?.projectId
            if (!projectId) return false
            try {
                return (
                    checkRole(projectId, 'gcb2:subscribersGrowth:view') &&
                    checkRole(projectId, 'project:connector:view')
                )
            } catch {
                return false
            }
        })

        const goToChatBotStatisticsReport = () => {
            const projectId = props.connector?.projectId
            const connectorId = props.connector?.connectorId
            if (!projectId || !connectorId) {
                root?.$store?.dispatch('callNotify', String('Не удалось перейти: нет данных канала'))
                return
            }
            if (!canOpenChatBotStatisticsReport.value) {
                root?.$store?.dispatch(
                    'callNotify',
                    String('Недостаточно прав: нужны просмотр отчёта и просмотр каналов проекта')
                )
                return
            }
            root?.$store?.commit('setChannelCreationMode', false)
            root?.$store?.commit('setChannelCreationStage', null)
            root?.$store?.commit('setIsConnectorSettingsSidebar', false)
            root?.$store?.commit('setConnectorSettingsSidebarId', null)
            root?.$router
                ?.push({
                    path: `/project/${projectId}/GCB2/subscribersGrowth`,
                    query: { botChannel: String(connectorId) },
                })
                ?.catch?.(() => {})
        }

        // Проверка, является ли это WhatsApp каналом
        const isWhatsappChannel = computed(() => {
            return props.providerType === PROVIDER_TYPES.whatsappBasis || 
                   props.providerType === PROVIDER_TYPES.whatsappApi ||
                   props.providerType === PROVIDER_TYPES.whatsapp
        })

        const isMaxChannel = computed(() => {
            return props.providerType === PROVIDER_TYPES.maxBasis ||
                   props.providerType === PROVIDER_TYPES.maxDh
        })

        const canChangeChannelProvider = computed(() => {
            // whatsappDh намеренно НЕ входит в isWhatsappChannel (там QR/auth-логика),
            // но свитч смены провайдера для него нужен: канал по умолчанию создаётся на нашем
            // провайдере, и клиент должен иметь возможность уйти на Провайдер 1/2 как фоллбэк.
            return isWhatsappChannel.value ||
                   isMaxChannel.value ||
                   props.providerType === PROVIDER_TYPES.whatsappDh
        })
        
        // Провайдеры, у которых доступен «Контроль переписки» через network-fixer controlPhone.
        const isControlAvailable = computed(() => {
            return BOT_PROVIDERS_WITH_CONTROL.includes(selectedBotProvider.value)
        })

        // Проверка, является ли это провайдером Data Heroes (whatsapp)
        const isDataHeroesProvider = computed(() => {
            return props.providerType === PROVIDER_TYPES.whatsapp
        })

        const isMaxBusinessProvider = computed(() => {
            return getBotProviderFromProviderType(props.providerType) === 'max-business'
        })

        const isMaxBusinessAuthorized = computed(() => {
            return isMaxBusinessProvider.value &&
                (qr.value.data?.type === 'alreadyLogged' || qr.value.data?.status === 'authorized')
        })

        const isMaxBusinessNotAuthorized = computed(() => {
            return isMaxBusinessProvider.value &&
                (qr.value.data?.type === 'notAuthorized' || qr.value.data?.status === 'notAuthorized')
        })

        const isSmsGateProvider = computed(() => {
            return getBotProviderFromProviderType(props.providerType) === 'sms-gate'
        })

        const isSmsGateConnected = computed(() => {
            return (
                isSmsGateProvider.value &&
                (qr.value.data?.type === 'alreadyLogged' ||
                    qr.value.data === 'authorized' ||
                    qr.value.data?.status === 'authorized') &&
                unAuthInProcess.value === false
            )
        })

        // Пугаем только при ЯВНОМ online:false. null (шлюз молчит) — не повод.
        const isSmsGateOffline = computed(() => {
            return isSmsGateProvider.value && smsGateOnline.value === false
        })

        const smsGateLastSeenText = computed(() => {
            if (!smsGateLastSeen.value) return ''
            const ts = new Date(smsGateLastSeen.value).getTime()
            if (Number.isNaN(ts)) return ''
            const min = Math.floor((Date.now() - ts) / 60000)
            if (min < 1) return 'меньше минуты назад'
            if (min < 60) return `${min} мин назад`
            const hours = Math.floor(min / 60)
            if (hours < 24) return `${hours} ч назад`
            return `${Math.floor(hours / 24)} дн назад`
        })

        // Сохраняем сразу по выбору — кнопки «Сохранить» на экране нет.
        const selectSmsGateSim = async (simNumber) => {
            const next = Number(simNumber)
            const sameSelected =
                smsGateSimNumber.value != null &&
                smsGateSimNumber.value !== '' &&
                next === Number(smsGateSimNumber.value)
            if (smsGateSimSaving.value || !Number.isFinite(next) || sameSelected) return
            const previous = smsGateSimNumber.value
            smsGateSimNumber.value = next
            smsGateSimSaving.value = true
            smsGateSimError.value = ''
            // Чистим поле ввода только при смене уже выбранной симки: номер предыдущей
            // симки в нём — прямой путь случайно сохранить его на другую SIM.
            if (previous != null && previous !== '' && Number(previous) !== next) {
                smsGatePhoneInput.value = ''
                smsGatePhoneError.value = ''
            }
            try {
                const res = await api.networkFixer.setChannelSim({
                    connectorId: props.connector.connectorId,
                    simNumber,
                })
                if (res?.error) {
                    smsGateSimNumber.value = previous
                    smsGateSimError.value = 'Не удалось сохранить выбор SIM'
                } else if (previous != null && previous !== '') {
                    // Номер у каждой симки свой. Подставляем сразу, иначе блок
                    // тестовой отправки живёт до следующего опроса статуса.
                    const nextPhone =
                        smsGateSimPhones.value?.[String(next)] || ''
                    smsGateChannelPhone.value = nextPhone
                    phone.value = nextPhone
                    phoneKey.value++
                    const currentConnector =
                        root.$store.getters['connectorById'](props.connector.connectorId) ||
                        props.connector
                    root.$store.commit('updateConnector', {
                        ...currentConnector,
                        phone: nextPhone,
                    })
                }
            } catch (e) {
                smsGateSimNumber.value = previous
                smsGateSimError.value = 'Не удалось сохранить выбор SIM'
            } finally {
                smsGateSimSaving.value = false
            }
        }

        const isSmsGateNotAuthorized = computed(() => {
            return isSmsGateProvider.value &&
                (qr.value.data?.type === 'notAuthorized' || qr.value.data?.status === 'notAuthorized')
        })

        const canSubmitSmsGateCredentials = computed(() => {
            return Boolean(
                smsGateUsername.value?.trim() &&
                    smsGatePassword.value?.trim() &&
                    smsGateDeviceId.value?.trim() &&
                    isValidPhone(smsGatePhone.value)
            )
        })

        // Опции провайдеров для WhatsApp — Провайдер 1, 2 и наш dh-whatsapp (Провайдер 3), из общего каталога.
        // Легаси-провайдер (старый DH-бот) в списке не показываем — он выпилен из подключения.
        const whatsappProviderOptions = computed(() => getProviderOptionsByGroup('whatsapp'))

        const maxProviderOptions = computed(() => getProviderOptionsByGroup('max'))

        const channelProviderOptions = computed(() => {
            if (isMaxChannel.value) {
                return maxProviderOptions.value
            }
            return whatsappProviderOptions.value
        })

        // Инициализация selectedProviderForChange на основе текущего providerType (только один раз)
        if (canChangeChannelProvider.value && props.providerType) {
            selectedProviderForChange.value = props.providerType
            // Сохраняем исходный провайдер
            if (!originalProviderType.value) {
                originalProviderType.value = props.providerType
            }
        }
        
        // Также проверяем connector.providerType как исходный провайдер (на случай если он был изменен ранее)
        if (!originalProviderType.value && props.connector?.providerType) {
            const connectorProviderType = props.connector.providerType
            // Если это WhatsApp провайдер, сохраняем его как исходный
            if (connectorProviderType === PROVIDER_TYPES.whatsappBasis || 
                connectorProviderType === PROVIDER_TYPES.whatsappApi ||
                connectorProviderType === PROVIDER_TYPES.whatsapp ||
                connectorProviderType === PROVIDER_TYPES.maxBasis ||
                connectorProviderType === PROVIDER_TYPES.maxDh) {
                originalProviderType.value = connectorProviderType
            }
        }

        // Обработчик смены провайдера (вызывается вручную, не через watcher)
        const changeProvider = async (newProviderType: string) => {
            // Если идет смена провайдера, игнорируем вызов
            if (providerChanging.value) {
                return
            }
            
            const oldProviderType = selectedProviderForChange.value
            
            // Если провайдер не изменился, не делаем ничего
            if (newProviderType === oldProviderType) {
                return
            }

            // Если канал авторизован, не позволяем менять провайдер
            if (isAuthorized.value) {
                root.$store.dispatch('callNotify', 'Нельзя менять провайдер для авторизованного канала. Сначала отключите канал.')
                // Откатываем изменение в UI
                selectedProviderForChange.value = oldProviderType
                return
            }
            
            // Проверка ограничений на смену провайдера на основе исходного провайдера
            const initialProvider = originalProviderType.value || props.providerType || oldProviderType
            
            // Если исходный провайдер был 3 (Data Heroes), нельзя менять на 1 или 2
            if (initialProvider === PROVIDER_TYPES.whatsapp) {
                if (newProviderType === PROVIDER_TYPES.whatsappBasis || newProviderType === PROVIDER_TYPES.whatsappApi) {
                    root.$store.dispatch('callNotify', 'Нельзя сменить провайдер с "Провайдер Data Heroes" на "Провайдер 1" или "Провайдер 2"')
                    // Откатываем изменение в UI
                    selectedProviderForChange.value = oldProviderType
                    return
                }
            }
            
            // Если исходный провайдер был 1 или 2, нельзя менять на 3
            if (initialProvider === PROVIDER_TYPES.whatsappBasis || initialProvider === PROVIDER_TYPES.whatsappApi) {
                if (newProviderType === PROVIDER_TYPES.whatsapp) {
                    root.$store.dispatch('callNotify', 'Нельзя сменить провайдер с "Провайдер 1" или "Провайдер 2" на "Провайдер Data Heroes"')
                    // Откатываем изменение в UI
                    selectedProviderForChange.value = oldProviderType
                    return
                }
            }
            

            // Начинаем процесс смены провайдера
            providerChanging.value = true
            
            // Обновляем selectedProviderForChange сразу, чтобы UI обновился
            selectedProviderForChange.value = newProviderType

            try {
                // Обновляем providerType через прямой API вызов
                const auth = getInstance()
                const token = await auth.getTokenSilently()
                
                // Пробуем использовать updateConnectorSettings с providerType в settings
                const url = `${cfg.apiEndpoint}/projects/updateConnectorSettings`
                const options = {
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ 
                        connectorId: props.connector.connectorId,
                        settings: { providerType: newProviderType }
                    }),
                }
                
                const response = await fetch(url, options)
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}))
                    throw new Error(errorData.message || 'Ошибка при обновлении провайдера')
                }
                const result = await response.json()
                
                if (result && result.connectorId) {
                    // Обновляем коннектор в store
                    root.$store.commit('updateConnector', result)
                }
                
                // Ждем, пока коннектор полностью обновится
                let attempts = 0
                const maxAttempts = 20
                let updated = false
                
                while (attempts < maxAttempts && !updated) {
                    const { data: updatedConnector, error } = await fetchConnector(props.connector.connectorId)
                    if (!error && updatedConnector && updatedConnector.providerType === newProviderType) {
                        // Обновляем коннектор в store
                        root.$store.commit('updateConnector', updatedConnector)
                        
                        // Обновляем selectedBotProvider на основе нового providerType
                        selectedBotProvider.value = getBotProviderFromProviderType(newProviderType)
                        
                        // Сбрасываем состояние QR и авторизации
                        qr.value.data = {}
                        phone.value = ''
                        phoneKey.value++
                        tgStatusChecked.value = false
                        
                        updated = true
                        root.$store.dispatch('callNotify', 'Провайдер успешно изменен')
                    } else {
                        await new Promise(resolve => setTimeout(resolve, 500))
                        attempts++
                    }
                }
                
                if (!updated) {
                    // Откатываем изменение, если не удалось обновить
                    selectedProviderForChange.value = oldProviderType
                    root.$store.dispatch('callNotify', 'Ошибка при изменении провайдера: таймаут обновления')
                }
            } catch (error: any) {
                console.error('Ошибка при изменении провайдера:', error)
                // Откатываем изменение
                selectedProviderForChange.value = oldProviderType
                const errorMessage = error?.message || 'Ошибка при изменении провайдера'
                root.$store.dispatch('callNotify', errorMessage)
            } finally {
                providerChanging.value = false
            }
        }

        // Обновляем selectedProviderForChange при изменении props.providerType (только если не идет смена)
        watch(
            () => props.providerType,
            (newProviderType) => {
                if (providerChanging.value) {
                    return
                }
                if (canChangeChannelProvider.value && newProviderType && selectedProviderForChange.value !== newProviderType) {
                    selectedProviderForChange.value = newProviderType
                }
            }
        )

        // Функция для получения телефона канала
        const fetchChannelPhone = async () => {
            // Для провайдера Data Heroes телефон приходит через websocket, не используем этот метод
            if (isDataHeroesProvider.value || isMaxBusinessProvider.value) {
                return
            }
            
            if (!isAuthorized.value || phone.value || unAuthInProcess.value) {
                return
            }
            
            try {
                const res = await api.networkFixer.getChannelPhone({ connectorId: props.connector.connectorId })
                if (!res?.error && res?.data?.phone) {
                    const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                    root.$store.commit('updateConnector', {
                        ...currentConnector,
                        phone: res.data.phone,
                    })
                    phone.value = res.data.phone
                    emit('input', Object.assign({}, props.value, { phone: res.data.phone }))
                    phoneKey.value++
                    // Останавливаем опрос, когда телефон получен
                    if (phonePollInterval.value) {
                        clearInterval(phonePollInterval.value)
                        phonePollInterval.value = null
                    }
                }
            } catch (e) {
                console.error('Ошибка при получении телефона канала:', e)
            }
        }

        // Запуск опроса телефона, когда канал авторизован, но телефона нет
        const startPhonePolling = () => {
            // Для провайдера Data Heroes телефон приходит через websocket, не используем опрос
            if (isDataHeroesProvider.value || isMaxBusinessProvider.value) {
                return
            }
            
            if (phonePollInterval.value) {
                return // Уже запущен
            }
            
            if (isAuthorized.value && !phone.value && !unAuthInProcess.value) {
                // Сразу делаем первый запрос
                fetchChannelPhone()
                // Затем опрашиваем каждые 3 секунды
                phonePollInterval.value = setInterval(() => {
                    fetchChannelPhone()
                }, 3 * 1000)
            }
        }

        // Остановка опроса телефона
        const stopPhonePolling = () => {
            if (phonePollInterval.value) {
                clearInterval(phonePollInterval.value)
                phonePollInterval.value = null
            }
        }

        // Hint texts
        const phoneHintText = `Номер телефона, с которого отсканирован QR-код, и с которого отправляются сообщения вашим гостям`
        const tgCodeHintText = `Если НЕ получается отсканировать QR-код, то скорее всего у вас включена двухфакторная авторизация в настройках вашего Telegram.
Для сканирования кода отключите ее по инструкции ниже:`
        const tgCodeHintTextCode = `Если НЕ получается привязать бота вводя код, то скорее всего у вас включена двухфакторная авторизация в настройках вашего Telegram.
Для привязки бота через код отключите ее по инструкции ниже:`
        const controlPhoneHintText = `Все входящии и исходящии сообщения на номере, к которому подключен бот, будут дублироваться в Telegram на номер, который вы укажите для осуществления контроля`
        const providerSelectHintText = computed(() => {
            const initialProvider = originalProviderType.value || props.providerType
            if (initialProvider === PROVIDER_TYPES.whatsapp) {
                return 'Провайдер Data Heroes работает только для маркетинговых рассылок. Смена провайдера возможна только до авторизации канала.'
            }
            return 'Выберите провайдер для WhatsApp канала. Смена провайдера возможна только до авторизации канала. Для смены провайдера отсканированного канала сначала отключите его.'
        })

        // Bot provider list - только для выбора, если providerType не задан (не должно быть в новом подходе)
        const botProvider = computed(() => {
            // Если providerType задан, определяем bot provider автоматически
            if (props.providerType) {
                const botProviderId = getBotProviderFromProviderType(props.providerType)
                return [{
                    id: botProviderId,
                    name: BOT_PROVIDER_TO_NAME_MAP[botProviderId] || botProviderId,
                }]
            }
            // В новом подходе providerType всегда должен быть задан
            return []
        })

        // YClients connector
        const yClientsConnector = computed<Connector>(() =>
            root.$store.getters
                .projectById(root.$router.currentRoute.params.id)
                .connectors.find((el: Connector) => el.connectorType === CONNECTOR_TYPES.yClients)
        )
        const controlButtonDisabled = computed(() => {
                const role = myRole()
                return !isValidPhone(phoneToControl.value) ||
                phoneToControlList.value.some(el => el.userId === phoneToControl.value) ||
                role !== 'owner'
        })

        const controlButtonDisabledHint = computed(() => {
                const role = myRole()
                if (role !== 'owner') {
                    return 'Обратитесь к вашему руководителю для подключения трансляции'
                }
                if (!isValidPhone(phoneToControl.value)) {
                    return 'Некорректный номер телефона'
                }
                if (phoneToControlList.value.some(el => el.userId === phoneToControl.value)) {
                    return 'Трансляция уже включена'
                }
        })

        onMounted(async () => {
            // Сброс дедупа целей Telegram при открытии настроек коннектора (новая попытка)
            tgYmGoalsSent.value = { qrShown: false, twoFa: false, authorized: false }
            root?.$root?.$on('reload-slot-billing-state', loadSlotFreezeState)
            await Promise.all([
                loadSlotFreezeState(),
                ensureProjectBillingLoaded(props.connector?.projectId, project.value),
            ])
            await loadWhatsappSyncModalPreference()
            // Определяем selectedBotProvider только на основе providerType из коннектора
            if (props.providerType) {
                // Для провайдера Data Heroes устанавливаем специальное значение
                if (props.providerType === PROVIDER_TYPES.whatsapp) {
                    selectedBotProvider.value = 'data-heroes'
                } else {
                    selectedBotProvider.value = getBotProviderFromProviderType(props.providerType)
                }
            }
            if (props.appCreated) {
                await checkAndHandleWaStatus()
            } else {
                initialStatusLoaded.value = true
                tryMarkSettingsDataReady()
            }
            
            // Сохраняем исходный провайдер из connector, если еще не сохранен
            if (!originalProviderType.value && props.connector?.providerType) {
                const connectorProviderType = props.connector.providerType
                // Если это WhatsApp провайдер, сохраняем его как исходный
                if (connectorProviderType === PROVIDER_TYPES.whatsappBasis || 
                    connectorProviderType === PROVIDER_TYPES.whatsappApi ||
                    connectorProviderType === PROVIDER_TYPES.whatsapp ||
                    connectorProviderType === PROVIDER_TYPES.maxBasis ||
                    connectorProviderType === PROVIDER_TYPES.maxDh) {
                    originalProviderType.value = connectorProviderType
                }
            }
            
            // Если исходный провайдер еще не определен, используем текущий providerType
            if (!originalProviderType.value && props.providerType) {
                originalProviderType.value = props.providerType
            }

            // Для провайдера Data Heroes добавляем обработчик websocket для авторизации
            if (isDataHeroesProvider.value) {
                // Проверяем телефон из коннектора при монтировании
                if (props.connector?.phone && !phone.value) {
                    phone.value = props.connector.phone
                    emit('input', Object.assign({}, props.value, { phone: props.connector.phone }))
                    phoneKey.value++
                }
                
                websocket.socket.on('WA_AUTH_SUCCESS', async (payload: any) => {
                    if (payload.connectorId !== props.connector.connectorId) return
                    const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                    const updatedConnector = {
                        ...currentConnector,
                        alive: true,
                        phone: payload.phone || currentConnector.phone,
                    }
                    root.$store.commit('updateConnector', updatedConnector)
                    // Используем телефон из payload или из коннектора
                    const phoneToUse = payload.phone || currentConnector.phone || props.connector.phone
                    if (phoneToUse && !phone.value) {
                        phone.value = phoneToUse
                        emit('input', Object.assign({}, props.value, { phone: phoneToUse }))
                        phoneKey.value++
                    }
                    qr.value.data = { type: 'alreadyLogged', status: 'authorized' }
                    emitAuthStatusChanged(true)
                })
            }

            // controlPhoneList (connectedPhone) для каналов (whatsappChannel и т.д.) в онбординге не поддерживается — не вызываем, чтобы не было 500
            if (!props.isOnboarding) {
                try {
                    const res = await api.networkFixer.controlPhoneList({ connectorId: props.connector.connectorId })
                    if (!res?.error) {
                        phoneToControlList.value = res?.data
                    }
                } catch (e) {
                    // игнорируем (например 500 для channel connector)
                }
            }

            // По умолчанию используем метод ввода кода
            telegramAuthMethod.value = 'qr'

            if (isSmsGateProvider.value) {
                resetSmsGateCredentialsFields()
                loadSmsGateQr()
                if (props.connector?.phone && !phone.value) {
                    phone.value = props.connector.phone
                    emit('input', Object.assign({}, props.value, { phone: props.connector.phone }))
                }
            }
            
            // Даем время на инициализацию компонента перед тем, как начать отслеживать изменения
            setTimeout(() => {
                isInitializing.value = false
            }, 2000)
            if (!props.appCreated) {
                initialStatusLoaded.value = true
                tryMarkSettingsDataReady()
            }
            if (!props.isOnboarding) {
                setTimeout(() => {
                    slotFreezeLoaded.value = true
                    initialStatusLoaded.value = true
                    tryMarkSettingsDataReady()
                }, 8000)
            }
        })

        onUnmounted(() => {
            root?.$root?.$off('reload-slot-billing-state', loadSlotFreezeState)
            setConnectorSidebarModalState(false)
            clearInterval(fetchQrInterval.value)
            stopPhonePolling()
            // Очищаем websocket listener для Data Heroes
            if (isDataHeroesProvider.value) {
                websocket.socket.off('WA_AUTH_SUCCESS')
            }
            // dh-telegram QR гасится по протуханию lease на воркере (поллинг остановлен выше) —
            // отдельный вызов clearQr не нужен.
        })
        watch(() => props.connector?.connectorId, () => {
            isWhatsappSyncModalPreferenceLoaded.value = false
            loadSlotFreezeState()
            loadWhatsappSyncModalPreference()
            if (isSmsGateProvider.value) {
                resetSmsGateCredentialsFields()
                loadSmsGateQr()
                if (props.connector?.phone && !phone.value) {
                    phone.value = props.connector.phone
                    emit('input', Object.assign({}, props.value, { phone: props.connector.phone }))
                }
            }
        })

        watch(
            () => tgSendCode.value.isSendCode,
            newValue => {
                if (newValue) {
                    tgSendCode.value.countdown = 60
                    // Фокусируемся на первом поле кода при показе блока
                    root.$nextTick(() => {
                        const firstInput = root.$refs['codeInput0'] as HTMLInputElement[]
                        if (firstInput && firstInput[0]) {
                            firstInput[0].focus()
                        }
                    })
                    let timer = setInterval(() => {
                        tgSendCode.value.countdown--
                        if (tgSendCode.value.countdown <= 0) {
                            clearInterval(timer)
                            tgSendCode.value.isSendCode = false
                            tgSendCode.value.error = null
                        }
                    }, 1000)
                }
            }
        )
        watch(
            () => maxSendCode.value.isSendCode,
            newValue => {
                let timer = setInterval(() => {
                    maxSendCode.value.countdown--
                    if (maxSendCode.value.countdown <= 0) {
                        clearInterval(timer)
                        maxSendCode.value.isSendCode = false
                    }
                }, 1000)
            }
        )

        watch(
            () => qr.value.loading,
            (loading) => {
                if (!loading && selectedBotProvider.value) {
                    initialStatusLoaded.value = true
                    tryMarkSettingsDataReady()
                }
            }
        )
        watch(
            () => props.appCreated,
            v => {
                if (v && !fetchQrInterval.value) {
                    setFetchQrInterval()
                }
            },
            { immediate: true }
        )
        watch(
            () => maxSendCode.value.code,
            v => {
                if (v && maxSendCode.value.sendCodeError) {
                    maxSendCode.value.sendCodeError = false
                }
            },
            { immediate: true }
        )
        const selectedBotProviderInited = ref(false)
        watch(
            () => selectedBotProvider.value,
            v => {
                qr.value.data = {}
                hasWhatsappQrInCurrentAuthFlow.value = false
                // Сбрасываем showSendBtn при изменении провайдера
                tgSendCode.value.showSendBtn = false
                maxSendCode.value.showSendBtn = false
                if(selectedBotProviderInited.value) {
                    // Сбрасываем таймеры последнего запроса при смене провайдера
                    lastTgQrRequestTime.value = null
                    lastMaxQrRequestTime.value = null
                    lastWa2QrRequestTime.value = null
                    lastDataHeroesQrRequestTime.value = null
                }
                if(v) {
                    selectedBotProviderInited.value = true
                }                
            },
            { immediate: true }
        )

        watch(
            () => telegramAuthMethod.value,
            () => {
                // Сбрасываем таймер последнего запроса при смене метода авторизации
                lastTgQrRequestTime.value = null
                // Сбрасываем поля кода при переключении на QR
                if (telegramAuthMethod.value === 'qr') {
                    codeDigits.value = ['', '', '', '', '']
                }
                // Пересоздаем интервал при изменении метода авторизации Telegram
                if (props.appCreated && (selectedBotProvider.value === 'wApi-telegram' || selectedBotProvider.value === 'dh-telegram') && fetchQrInterval.value) {
                    setFetchQrInterval()
                }
            }
        )
        
        
        // Очистка ошибки при изменении телефона
        watch(
            () => tgSendCode.value.phone,
            () => {
                if (tgSendCode.value.error) {
                    tgSendCode.value.error = null
                }
            }
        )
        
        // Синхронизация codeDigits с tgSendCode.code при изменении извне
        watch(
            () => tgSendCode.value.code,
            (newCode) => {
                if (newCode && newCode.length <= 5) {
                    const digits = newCode.split('')
                    codeDigits.value = [...digits, ...Array(5 - digits.length).fill('')].slice(0, 5)
                } else if (!newCode) {
                    codeDigits.value = ['', '', '', '', '']
                }
            }
        )
        
        // Автоматическая отправка кода при заполнении всех 5 цифр
        watch(
            () => tgSendCode.value.code,
            async (newCode, oldCode) => {
                // Проверяем, что код полностью заполнен (5 цифр), это Telegram, и код был отправлен
                // Также проверяем, что код действительно изменился (не пустой и не равен предыдущему)
                if (
                    newCode &&
                    newCode.length === 5 &&
                    /^\d{5}$/.test(newCode) && // Проверяем, что это 5 цифр
                    (selectedBotProvider.value === 'wApi-telegram' || selectedBotProvider.value === 'dh-telegram') &&
                    tgSendCode.value.isSendCode &&
                    newCode !== oldCode // И код действительно новый
                ) {
                    // Вызываем inputCode
                    await inputCode()
                    // Очищаем код после отправки
                    root.$nextTick(() => {
                        codeDigits.value = ['', '', '', '', '']
                        tgSendCode.value.code = ''
                    })
                }
            }
        )

        // Отслеживаем изменения статуса авторизации и телефона для запуска/остановки опроса
        watch(
            [() => isAuthorized.value, () => phone.value, () => unAuthInProcess.value],
            () => {
                if (isAuthorized.value && !phone.value && !unAuthInProcess.value && !isMaxBusinessProvider.value) {
                    startPhonePolling()
                } else {
                    stopPhonePolling()
                }
            },
            { immediate: true }
        )

        // Отслеживаем переход из неавторизованного состояния в авторизованное для автоматического сохранения
        watch(
            () => isAuthorized.value,
            (newValue, oldValue) => {
                // Пропускаем первый вызов (когда oldValue === undefined)
                if (oldValue === undefined) {
                    // Сохраняем начальное состояние авторизации
                    wasAuthorizedOnMount.value = newValue
                    if (newValue) {
                        emitAuthorizedDuringCreation()
                    }
                    
                    // Если в коннекторе alive: true, но канал не авторизован, обновляем store
                    if (!newValue && props.connector?.alive === true) {
                        const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                        const updatedConnector = {
                            ...currentConnector,
                            alive: false
                        }
                        root.$store.commit('updateConnector', updatedConnector)
                    }
                    
                    // Даем время на инициализацию компонента
                    setTimeout(() => {
                        isInitializing.value = false
                    }, 2000)
                    return
                }
                
                // Всегда обновляем поле alive в store при изменении статуса авторизации
                if (newValue === true && oldValue === false) {
                    // Не обновляем alive, если идет процесс отключения
                    if (unAuthInProcess.value) {
                        return
                    }
                    // Получаем актуальный коннектор из store
                    const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                    // Обновляем поле alive в коннекторе в store, сохраняя позицию
                    const updatedConnector = {
                        ...currentConnector,
                        alive: true
                    }
                    root.$store.commit('updateConnector', updatedConnector)
                    emitAuthStatusChanged(true)
                    showWhatsappSyncModalIfNeeded()
                    emitAuthorizedDuringCreation()
                    // При онбординге: после логина автоматически переходим на следующий этап — настройки
                    if (props.isOnboarding && !hasSavedOnAuth.value) {
                        hasSavedOnAuth.value = true
                        emit('saved')
                    }
                }
                // Если авторизация пропала, сбрасываем флаг сохранения и поле alive
                if (newValue === false && oldValue === true) {
                    hasSavedOnAuth.value = false
                    wasAuthorizedOnMount.value = false
                    hasWhatsappQrInCurrentAuthFlow.value = false
                    // Получаем актуальный коннектор из store
                    const currentConnector = root.$store.getters['connectorById'](props.connector.connectorId) || props.connector
                    const updatedConnector = {
                        ...currentConnector,
                        alive: false
                    }
                    root.$store.commit('updateConnector', updatedConnector)
                    emitAuthStatusChanged(false)
                }
            }
        )

        const goToProject = async () => {
            root.$router.push(`/project/${props.connector.projectId}`)
        }

        return {
            emit,
            testMessage,
            sendTestMesage,
            onSmsWizardTest,
            get,
            qr,
            selectedBotProvider,
            tgSendCode,
            sendCode,
            inputCode,
            submitTgDhPassword,
            unAuthInstance,
            unAuthInProcess,
            connectedBot,
            phoneValidator,
            isValidPhone,
            yClientsConnector,
            phoneToControl,
            controlButtonDisabled,
            controlPhoneValidator,
            controlButtonDisabledHint,
            controlOn,
            phoneToControlKey,
            isControlErrorShow,
            phoneToControlList,
            controlOff,
            phoneKey,
            phone,
            shareQrCode,
            isDhMigration,
            onboardingQrSteps,
            phoneHintText,
            tgCodeHintText,
            tgCodeHintTextCode,
            maxSendCode,
            controlPhoneHintText,
            botProvider,
            goToProject,
            isWhatsappChannel,
            isMaxChannel,
            isControlAvailable,
            whatsappProviderOptions,
            channelProviderOptions,
            selectedProviderForChange,
            providerChanging,
            isSettingsDataReady,
            providerSelectHintText,
            isAuthorized,
            changeProvider,
            canChangeChannelProvider,
            isDataHeroesProvider,
            formattedPhone,
            effectivePhone,
            formattedEffectivePhone,
            formattedTgSendCodePhone,
            showSlotBillingBanner,
            slotBillingBannerMode,
            slotBillingBannerText,
            slotBillingBannerDate,
            slotNextPaymentShortLabel,
            currentSlotAutoRenewalEnabled,
            currentSlotAutoRenewalMenuText,
            currentSlotIsTariff,
            openTariffBilling,
            onBannerChangeChannel,
            onBannerCancelSubscription,
            isDetachChannelModalOpened,
            detachChannelModalLoading,
            detachChannelModalChannelName,
            closeDetachChannelModal,
            handleDetachChannelCreatePackage,
            tg1CodeWaiting,
            tg1CodeSending,
            telegramAuthMethod,
            telegramCodeAuthEnabled,
            onTelegramAuthMethodChange,
            isWhatsappSyncModalOpened,
            hideWhatsappSyncModal,
            setWhatsappSyncModalPreference,
            closeWhatsappSyncModal,
            handleWhatsappSyncModalInput,
            codeDigits,
            handleCodeBeforeInput,
            handleCodeFocus,
            handleCodeInput,
            handleCodeKeydown,
            handleCodePaste,
            slotBillingBannerWarningText,
            showChatBotStatisticsPromo,
            canOpenChatBotStatisticsReport,
            goToChatBotStatisticsReport,
            maxBusinessToken,
            maxBusinessTokenError,
            maxBusinessTokenLoading,
            connectMaxBusinessByToken,
            isMaxBusinessProvider,
            isMaxBusinessAuthorized,
            isMaxBusinessNotAuthorized,
            smsGateUsername,
            smsGatePassword,
            smsGateDeviceId,
            smsGatePhone,
            smsGateUsernameReadonly,
            smsGatePasswordReadonly,
            unlockSmsGateUsername,
            unlockSmsGatePassword,
            smsGateCredentialsError,
            smsGateCredentialsLoading,
            connectSmsGateByCredentials,
            smsGateQrDataUrl,
            smsGateQrLoading,
            smsGateQrError,
            smsGateChannelPhone,
            smsGateStatusLoaded,
            smsGateDeviceName,
            smsGateSimCards,
            smsGateSimNumber,
            smsGateSimPhones,
            smsGateSimSaving,
            smsGateSimError,
            selectSmsGateSim,
            isSmsGateConnected,
            isSmsGateOffline,
            smsGateLastSeenText,
            smsGatePhoneInput,
            smsGatePhoneSaving,
            smsGatePhoneError,
            saveSmsGatePhone,
            loadSmsGateQr,
            isSmsGateProvider,
            isSmsGateNotAuthorized,
            canSubmitSmsGateCredentials,
            getBotProviderFromProviderType,
        }
    },
})
